import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import {
  handleApiError,
  handleAuthError,
  handleGenericError,
  handleError,
} from "../../utils/errorHandler";
import {
  Grid,
  Card,
  Text,
  Group,
  Button,
  Stack,
  Loader,
  Alert,
  Box,
  Title,
  ThemeIcon,
  Modal,
  PinInput,
  Divider,
  TextInput,
  ActionIcon,
  Menu,
  Progress,
  Badge,
  Paper,
  Select,
  Table,
  Avatar,
  Drawer,
  SegmentedControl,
} from "@mantine/core";
import {
  IconUsers,
  IconUserCheck,
  IconClock,
  IconCurrencyDollar,
  IconChartLine,
  IconTrendingUp,
  IconHeart,
  IconWallet,
  IconPlus,
  IconRefresh,
  IconAlertCircle,
  IconLock,
  IconShieldCheck,
  IconInfoCircle,
  IconSearch,
  IconX,
  IconEye,
  IconEyeOff,
  IconChevronDown,
  IconSettings,
  IconPercentage,
  IconActivity,
  IconTargetArrow,
  IconCheck,
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
  IconPhoto,
  IconVideo,
  IconFileText,
  IconClipboardList,
  IconPlayerPlay,
  IconArrowRight,
  IconSparkles,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import DefaultScaleModal from "./DefaultScaleModal";
import RepDetailView from "./RepDetailView";
import RepCardList from "./RepCardList";
import RepDetailsPage from "./RepDetailsPage";
import { prefetchRepDetails } from "../../utils/repDetailsCache";
import { downlineRevenue } from "../../utils/downlineRevenue";
import RepTrainingPage from "../Training/RepTrainingPage";
import TaskApprovalDetailModal from "./TaskApprovalDetailModal";
import PitchReviewPanel from "./PitchReviewPanel";
import RepHealthTab from "./RepHealthTab";
import RevenueTab from "./RevenueTab";
import BudgetTab from "./BudgetTab";
import CreateTeamEventModal from "../Organization/CreateTeamEventModal";
import QuickCreateEventPopup from "../Organization/QuickCreateEventPopup";
import CalendarFilterModal from "../Organization/CalendarFilterModal";
import CreateEventModal from "../Attendance/CreateEventModal";
import AttendanceEventDetailModal from "../Attendance/EventDetailModal";
import { Calendar, momentLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "../Organization/OrganizationPage.css";
import {
  DEFAULT_PIN_UNLOCK_DURATION,
  PIN_UNLOCK_DURATIONS,
  isPinUnlockSessionActive,
  pinUnlockHeaders,
  startPinUnlockSession,
} from "../../utils/pinUnlockSession";
import { attendanceService } from "../../services/attendanceService";

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

function DownlineManagementPage() {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The rep whose details are open in the right-side drawer (null = closed).
  const [detailRepId, setDetailRepId] = useState(null);
  // The list row's rep object, handed to the drawer so it can paint the header
  // immediately while the full profile loads.
  const [detailRep, setDetailRep] = useState(null);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalReps, setTotalReps] = useState(0);
  const [totalActiveReps, setTotalActiveReps] = useState(0);
  const [totalPendingReps, setTotalPendingReps] = useState(0);
  const [projectedRevenue, setProjectedRevenue] = useState(0);
  const [averageProjectedMargin, setAverageProjectedMargin] = useState(0);
  const [projectedEarnings, setProjectedEarnings] = useState(0);
  const [overviewHealthData, setOverviewHealthData] = useState(null);
  const [overviewPendingTasks, setOverviewPendingTasks] = useState(0);
  const [overviewPendingPitches, setOverviewPendingPitches] = useState(0);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewPeriod, setOverviewPeriod] = useState("month");
  const [overviewComparison, setOverviewComparison] = useState({
    current: null,
    previous: null,
  });
  const [overviewTasksList, setOverviewTasksList] = useState([]);
  const [overviewPitchesList, setOverviewPitchesList] = useState([]);
  const [overviewAttentionData, setOverviewAttentionData] = useState(null);

  const OVERVIEW_PERIOD_DAYS = {
    day: 1,
    week: 7,
    month: 30,
    quarter: 90,
    year: 365,
  };

  const OVERVIEW_COMPARISON_LABELS = {
    day: "vs yesterday",
    week: "vs last week",
    month: "vs last month",
    quarter: "vs last quarter",
    year: "vs last year",
  };

  const OVERVIEW_PERIOD_LABELS = {
    day: "Today",
    week: "This Week",
    month: "This Month",
    quarter: "This Quarter",
    year: "This Year",
  };

  const getOverviewPeriodLabel = period =>
    OVERVIEW_PERIOD_LABELS[period] || "This Period";

  // Format currency helper function
  const formatCurrency = value => {
    if (value === null || value === undefined || value === "") return "N/A";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  // Format percentage helper function
  const formatPercentage = value => {
    if (value === null || value === undefined || value === "") return "N/A";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "N/A";
    // Only show decimal if it's not .0
    const formatted =
      numValue % 1 === 0 ? numValue.toFixed(0) : numValue.toFixed(1);
    return formatted + "%";
  };

  const getProgramCompletionPercent = rep => {
    const totalAssigned = Number(rep.total_programs_assigned) || 0;
    const completed = Number(rep.programs_completed) || 0;

    if (totalAssigned <= 0) return 0;
    return Math.round((completed / totalAssigned) * 100);
  };

  const [isDefaultPayScalesModalOpen, setIsDefaultPayScalesModalOpen] =
    useState(false);
  const [isDefaultOverridesModalOpen, setIsDefaultOverridesModalOpen] =
    useState(false);
  const [isDefaultMDModalOpen, setIsDefaultMDModalOpen] = useState(false);
  const [resendingInvite, setResendingInvite] = useState(null);
  const [showPayDetails, setShowPayDetails] = useState(false);
  // Which figures the roster shows: "projected" (season projection) or
  // "current" (revenue on the books so far — FieldRoutes sales plus manually
  // recorded revenue — and the earnings the engine has computed on it).
  const [payMode, setPayMode] = useState("current");
  // The logged-in user's own headline figures from the roster response
  // (summary_metrics.caller); pay fields are null until the PIN is entered.
  const [callerMetrics, setCallerMetrics] = useState(null);
  const [viewingRepDetail, setViewingRepDetail] = useState(null);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [pinVerifying, setPinVerifying] = useState(false);
  const [pinError, setPinError] = useState(null);
  const [pinVerified, setPinVerified] = useState(false);
  const [unlockDuration, setUnlockDuration] = useState(
    String(DEFAULT_PIN_UNLOCK_DURATION)
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState(null); // null = all, 'active', 'pending'

  // Seasons state
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonIds, setSelectedSeasonIds] = useState([]);
  const [seasonsLoading, setSeasonsLoading] = useState(false);

  // Pay Condition state
  const [incentiveCostPercent, setIncentiveCostPercent] = useState(0);
  const [bufferCostPercent, setBufferCostPercent] = useState(0);
  const [isPayConditionsModalOpen, setIsPayConditionsModalOpen] =
    useState(false);
  const [tempIncentiveCost, setTempIncentiveCost] = useState(0);
  const [tempBufferCost, setTempBufferCost] = useState(0);
  const [isSavingPayConditions, setIsSavingPayConditions] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState("my-downline");

  // Events tab state
  const calendarRef = useRef(null);
  const calendarWrapperRef = useRef(null);
  const [calendarEvents, setCalendarEvents] = useState([]);

  // Override the native browser drag ghost so only a small "Moving event…" pill
  // appears during a drag — instead of a screenshot of the entire events column.
  useEffect(() => {
    const el = calendarWrapperRef.current;
    if (!el) return;

    const handleDragStart = e => {
      if (!e.target.closest(".rbc-event")) return;
      const ghost = document.createElement("div");
      ghost.style.cssText = [
        "position:fixed",
        "top:-200px",
        "width:160px",
        "height:34px",
        "background:#d0521f",
        "border-radius:6px",
        "color:#fff",
        "font-size:13px",
        "font-weight:600",
        "display:flex",
        "align-items:center",
        "justify-content:center",
        "padding:0 12px",
        "box-shadow:0 4px 12px rgba(0,0,0,0.2)",
        "pointer-events:none",
      ].join(";");
      ghost.textContent = "Moving event…";
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 80, 17);
      requestAnimationFrame(() => document.body.removeChild(ghost));
    };

    el.addEventListener("dragstart", handleDragStart);
    return () => el.removeEventListener("dragstart", handleDragStart);
  }, []);
  const [attendanceEvents, setAttendanceEvents] = useState([]);
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());
  const [currentCalendarView, setCurrentCalendarView] = useState("day");
  const [eventPrefillDateTime, setEventPrefillDateTime] = useState(null);
  const [eventPrefillMeetingType, setEventPrefillMeetingType] =
    useState("team_meeting");
  const [createTeamEventModalOpen, setCreateTeamEventModalOpen] =
    useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Attendance event modal state
  const [showCreateAttendance, setShowCreateAttendance] = useState(false);
  const [selectedAttendanceEvent, setSelectedAttendanceEvent] = useState(null);
  const [attendanceView, setAttendanceView] = useState("upcoming");
  const [attendanceCounts, setAttendanceCounts] = useState({});

  // Quick-create popup (calendar drag-to-create)
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickCreateSlot, setQuickCreateSlot] = useState(null);
  // Ghost/draft event shown on the calendar while popup is open
  const [draftCalendarEvent, setDraftCalendarEvent] = useState(null);

  const getEventPrefillDateTime = dateValue => {
    const date = dateValue ? new Date(dateValue) : new Date();
    // If user taps a day cell (often midnight), prefill to 9:00 AM
    if (date.getHours() === 0 && date.getMinutes() === 0) {
      date.setHours(9, 0, 0, 0);
    }
    return date;
  };

  // Program data state (for Program Tracking tab)
  const [programData, setProgramData] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);
  const [programError, setProgramError] = useState(null);
  // Separate loading flag for the expensive avg-days KPI (background computation)
  const [programAvgDaysLoading, setProgramAvgDaysLoading] = useState(false);
  const [programTrackingMetrics, setProgramTrackingMetrics] = useState({
    completionRate: 0,
    activeLearners7d: 0,
    atRiskReps: 0,
    avgDaysToCompleteProgram: null,
  });
  const [programPage, setProgramPage] = useState(0);
  const PROGRAMS_PER_PAGE = 4;

  // Task approval detail modal state
  const [showApprovalDetailModal, setShowApprovalDetailModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [expandedProgramId, setExpandedProgramId] = useState(null);

  // Rep detail modal state
  const [repDetailModal, setRepDetailModal] = useState(null);
  const [repDetailData, setRepDetailData] = useState(null);
  const [repDetailLoading, setRepDetailLoading] = useState(false);

  const getAttendanceEventEndTime = event => {
    const scheduledTime = new Date(event.scheduled_time);
    const durationMinutes = Number(event.duration_minutes || 60);
    return new Date(scheduledTime.getTime() + durationMinutes * 60000);
  };

  const sortedSidebarEvents = useMemo(() => {
    const teamEvents = calendarEvents
      .filter(event => event?.start)
      .map(event => ({
        id: `team-${event.id ?? event.group_event_id ?? event.start?.toISOString?.()}`,
        source: "team",
        title: event.title || "Untitled Event",
        scheduled_time: event.start,
        end_time: event.end,
        location: event.location || null,
        status: String(event.status || "active").toLowerCase(),
        attendees: Array.isArray(event.attendees) ? event.attendees : [],
        rawEvent: event,
      }));

    const attendanceEventCards = attendanceEvents
      .filter(event => event?.scheduled_time)
      .map(event => ({
        id: `attendance-${event.id}`,
        source: "attendance",
        title: event.title || "Untitled Event",
        scheduled_time: event.scheduled_time,
        end_time: getAttendanceEventEndTime(event),
        location: event.location || null,
        status: String(event.status || "active").toLowerCase(),
        attendees: [],
        rawEvent: event,
      }));

    return [...teamEvents, ...attendanceEventCards].sort(
      (a, b) =>
        new Date(a.scheduled_time).getTime() -
        new Date(b.scheduled_time).getTime()
    );
  }, [calendarEvents, attendanceEvents]);

  const filteredAttendanceList = useMemo(() => {
    const now = new Date();
    return sortedSidebarEvents.filter(event => {
      const normalizedStatus = String(event?.status || "").toLowerCase();
      if (attendanceView === "upcoming") {
        if (
          normalizedStatus === "completed" ||
          normalizedStatus === "cancelled"
        ) {
          return false;
        }
      }
      const eventEnd = event.end_time
        ? new Date(event.end_time)
        : getAttendanceEventEndTime(event.rawEvent || event);
      return attendanceView === "upcoming" ? eventEnd >= now : eventEnd < now;
    });
  }, [sortedSidebarEvents, attendanceView]);

  const eventsOverviewMetrics = useMemo(() => {
    const now = new Date();

    const upcomingEvents = sortedSidebarEvents.filter(event => {
      const eventEnd = event.end_time
        ? new Date(event.end_time)
        : getAttendanceEventEndTime(event.rawEvent || event);
      return eventEnd >= now;
    }).length;

    const pastEvents = sortedSidebarEvents.length - upcomingEvents;

    const attendanceCheckIns = attendanceEvents.reduce(
      (sum, event) => sum + (attendanceCounts[event.id] || 0),
      0
    );

    return {
      totalEvents: sortedSidebarEvents.length,
      upcomingEvents,
      pastEvents,
      attendanceCheckIns,
    };
  }, [sortedSidebarEvents, attendanceEvents, attendanceCounts]);
  const totalInviteableParticipants = useMemo(() => {
    const seen = new Set();
    const flatten = repsArray => {
      repsArray.forEach(rep => {
        const key = rep.user_season_id || rep.rep_id || rep.id;
        if (key != null) seen.add(String(key));
        if (Array.isArray(rep.reps) && rep.reps.length > 0) {
          flatten(rep.reps);
        }
      });
    };

    flatten(reps);
    return seen.size;
  }, [reps]);

  useEffect(() => {
    let isMounted = true;

    const loadAttendanceCounts = async () => {
      if (!attendanceEvents.length) {
        if (isMounted) setAttendanceCounts({});
        return;
      }

      try {
        const jwt = await getVerifiedJWT();
        const entries = await Promise.all(
          attendanceEvents.map(async event => {
            try {
              const res = await attendanceService.getEventAttendees(
                event.id,
                jwt
              );
              const attendees =
                res?.success && Array.isArray(res.data)
                  ? res.data
                  : Array.isArray(res)
                    ? res
                    : [];
              return [event.id, attendees.length];
            } catch {
              return [event.id, 0];
            }
          })
        );

        if (isMounted) {
          setAttendanceCounts(Object.fromEntries(entries));
        }
      } catch {
        if (isMounted) setAttendanceCounts({});
      }
    };

    loadAttendanceCounts();

    return () => {
      isMounted = false;
    };
  }, [attendanceEvents, getVerifiedJWT]);

  // Tab definitions
  const TABS = [
    // { id: "overview", label: "Overview", icon: IconNetwork },
    { id: "my-downline", label: "My Downline", icon: IconUsers },
    // { id: "revenue", label: "Revenue", icon: IconTrendingUp },
    { id: "budget", label: "Budget", icon: IconWallet },
    // { id: "rep-health", label: "Rep Health", icon: IconHeart },
    // { id: "training-stats", label: "Training Stats", icon: IconActivity },
    // { id: "programs", label: "Program Tracking", icon: IconTargetArrow },
    // { id: "events", label: "Events", icon: IconCalendarEvent },
    // { id: "pitch-reviews", label: "Pitch Reviews", icon: IconVideo },
  ];

  // Fetch team events for Events tab
  const fetchTeamEvents = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint("/v1/attendance/team-events"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const eventsData = result.data || [];

        const calendarFormattedEvents = eventsData.map(event => ({
          // Backend returns grouped team events, not a direct `id` field.
          // Use the first underlying meeting_id as the stable patch target.
          id: event.meeting_id || event.attendees?.[0]?.meeting_id || null,
          title: event.event_title || event.title || "Untitled Event",
          start: new Date(event.scheduled_time),
          end: new Date(
            new Date(event.scheduled_time).getTime() +
              (event.duration_minutes || 60) * 60000
          ),
          description: event.event_description,
          location: event.location,
          group_event_id: event.group_event_id,
          attendees: event.attendees || [],
          status: (() => {
            const attendeeStatuses = (event.attendees || []).map(attendee =>
              String(attendee?.status || "scheduled").toLowerCase()
            );
            if (attendeeStatuses.length === 0) return "active";
            if (attendeeStatuses.every(status => status === "cancelled")) {
              return "cancelled";
            }
            if (
              attendeeStatuses.every(status =>
                ["completed", "cancelled", "no_show"].includes(status)
              )
            ) {
              return "completed";
            }
            return "active";
          })(),
        }));

        setCalendarEvents(
          calendarFormattedEvents.filter(event => event.id != null)
        );
        return true;
      }
    } catch {
      setCalendarEvents([]);
    }
    return false;
  };

  const fetchAttendanceEvents = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const result = await attendanceService.getAttendanceEvents(jwt);
      if (result?.success && Array.isArray(result.data)) {
        setAttendanceEvents(result.data);
      } else if (Array.isArray(result)) {
        setAttendanceEvents(result);
      } else {
        setAttendanceEvents([]);
      }
    } catch {
      setAttendanceEvents([]);
    }
  };

  // Fetch seasons from API
  const fetchSeasons = async () => {
    try {
      setSeasonsLoading(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/seasons/all"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const seasonsData = result.data || [];
        setSeasons(seasonsData);

        // Default select all active seasons
        const activeSeasonIds = seasonsData
          .filter(season => season.is_active)
          .map(season => season.id);
        setSelectedSeasonIds(activeSeasonIds);
      }
    } catch (err) {
      showError(err.message ?? "Failed to get seasons");
    } finally {
      setSeasonsLoading(false);
    }
  };

  // Fetch reps from API
  // Flips whether a rep earns backend this season, then reloads the roster so
  // the manager's margin on that rep's revenue shows the rolled-up figure.
  const handleSetBackendEligible = async (rep, eligible) => {
    const jwt = await getVerifiedJWT();
    if (!jwt) return;
    const response = await fetch(
      getApiEndpoint(`/v1/rep/${rep.rep_id}/backend-eligibility`),
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ backend_eligible: eligible }),
      }
    );
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      const err = new Error(payload.message || `HTTP ${response.status}`);
      handleError(err, "Failed to update backend eligibility");
      throw err;
    }
    await fetchReps();
  };

  const fetchReps = async () => {
    try {
      setError(null);
      setLoading(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Failed to fetch reps");
        return;
      }

      const response = await fetch(
        getApiEndpoint("/v2/company-downline/reps-by-manager"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
            // The roster always comes back; the earnings and margin figures on
            // it are null unless this carries a live PIN unlock grant.
            ...pinUnlockHeaders(),
          },
          body: JSON.stringify({
            season_ids: selectedSeasonIds,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const summary_data = result.data.summary_metrics;
        const reps = result.data.reps ?? [];
        setReps(reps);
        setTotalReps(summary_data.total_reps ?? 0);
        setTotalActiveReps(summary_data.total_active_reps ?? 0);
        setTotalPendingReps(summary_data.total_pending_reps ?? 0);
        setProjectedRevenue(summary_data.projected_revenue ?? 0);
        setAverageProjectedMargin(summary_data.projected_average_margin ?? 0);
        setProjectedEarnings(summary_data.projected_earnings ?? 0);
        setCallerMetrics(summary_data.caller ?? null);
        setError(null);
      } else {
        setError("Failed to fetch reps");
      }
    } catch (err) {
      setError("Failed to fetch reps");
    } finally {
      setLoading(false);
    }
  };

  // Resend invite to rep
  const handleResendInvite = async repId => {
    try {
      setResendingInvite(repId);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(getApiEndpoint(`/v1/rep/invite/${repId}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        showSuccess("Invite resent successfully!");
      } else {
        handleApiError(
          new Error(`HTTP ${response.status} - ${response.statusText}`),
          "Failed to resend invite"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to resend invite");
    } finally {
      setResendingInvite(null);
    }
  };

  // Navigate to pitch review page
  const handlePitchReview = pitchReviewChannelId => {
    navigate(`/pitch-review/${pitchReviewChannelId}`);
  };

  // Handle pay details toggle - show PIN modal if not verified
  const handlePayDetailsToggle = () => {
    if (!showPayDetails) {
      if (isPinUnlockSessionActive()) {
        setPinVerified(true);
        setShowPayDetails(true);
        return;
      }

      // User is trying to enable pay details - require PIN when session is not active
      setIsPinModalOpen(true);
    } else {
      // User is hiding pay details
      setShowPayDetails(false);
    }
  };

  // Handle PIN verification
  const handleVerifyPin = async (pinValue = pin) => {
    try {
      setPinVerifying(true);
      setPinError(null);

      // Validate PIN
      if (!pinValue || pinValue.length !== 4) {
        setPinError("Please enter a 4-digit PIN");
        return;
      }

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setPinError("Authentication failed");
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/user/verify-pin"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          pin: parseInt(pinValue),
          duration_minutes: Number(unlockDuration),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data.verified && data.data.pin_token) {
          // Success - PIN verified. The grant is what unlocks the pay figures
          // server-side, so re-read the roster with it before revealing them.
          startPinUnlockSession({
            token: data.data.pin_token,
            expiresIn: data.data.expires_in,
            durationMinutes: Number(unlockDuration),
          });
          setPinVerified(true);
          setShowPayDetails(true);
          setIsPinModalOpen(false);
          setPin(""); // Clear PIN for security
          fetchReps();
        } else {
          setPinError("Invalid PIN. Please try again.");
          setPin(""); // Clear PIN on failure
        }
      } else {
        const data = await response.json();
        setPinError(data.message || "Invalid PIN. Please try again.");
        setPin(""); // Clear PIN on failure
      }
    } catch (err) {
      setPinError("Failed to verify PIN. Please try again.");
      setPin(""); // Clear PIN on failure
    } finally {
      setPinVerifying(false);
    }
  };

  // Handle PIN complete (auto-submit)
  const handlePinComplete = value => {
    setPin(value);
    // Auto-submit when PIN is complete
    if (value.length === 4) {
      // Small delay to show the complete PIN before verifying
      setTimeout(() => {
        handleVerifyPin(value);
      }, 300);
    }
  };

  // Handle PIN modal close
  const handleClosePinModal = () => {
    setIsPinModalOpen(false);
    setPin("");
    setPinError(null);
    setUnlockDuration(String(DEFAULT_PIN_UNLOCK_DURATION));
    // Make sure toggle is off if PIN was not verified
    if (!pinVerified) {
      setShowPayDetails(false);
    }
  };

  useEffect(() => {
    if (!showPayDetails) return undefined;

    const interval = setInterval(() => {
      if (!isPinUnlockSessionActive()) {
        setShowPayDetails(false);
        setPinVerified(false);
        // Re-read without the grant so the pay figures leave memory too, not
        // just the screen.
        fetchReps();
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPayDetails]);

  // Filter reps by status (active/pending) and search query - flatten hierarchy to show only matching reps
  const filterReps = (reps, query, statusFilter) => {
    const lowerQuery = query.trim().toLowerCase();

    // If no filters are applied, return the original hierarchical structure
    if (!lowerQuery && !statusFilter) {
      return reps;
    }

    const matchingReps = [];

    // Recursive function to collect all matching reps
    const collectMatches = repsArray => {
      repsArray.forEach(rep => {
        const fullName = `${rep.first_name} ${rep.last_name}`.toLowerCase();

        // Check if rep matches status filter
        // Use 'verified' field to match the badge logic (VERIFIED vs PENDING)
        const matchesStatus =
          !statusFilter ||
          (statusFilter === "active" && rep.verified) ||
          (statusFilter === "pending" && !rep.verified);

        // Check if rep matches search query
        const matchesSearch = !lowerQuery || fullName.includes(lowerQuery);

        // If this rep matches both filters, add it (without nested reps to show it unwrapped)
        if (matchesStatus && matchesSearch) {
          matchingReps.push({
            ...rep,
            reps: [], // Remove nested structure for search results
          });
        }

        // Continue searching in this rep's downline
        if (rep.reps && rep.reps.length > 0) {
          collectMatches(rep.reps);
        }
      });
    };

    collectMatches(reps);
    return matchingReps;
  };

  // Get filtered reps
  const filteredReps = filterReps([...reps], searchQuery, filterType);

  // Sort: 1) largest downline revenue, 2) largest team size, 3) largest
  // personal revenue — all in whichever mode (projected / current) is showing.
  const revenueField =
    payMode === "current" ? "total_revenue" : "projected_revenue";
  const sortedReps = [...filteredReps].sort((a, b) => {
    const aTeamRev = downlineRevenue(a, revenueField);
    const bTeamRev = downlineRevenue(b, revenueField);
    if (bTeamRev !== aTeamRev) return bTeamRev - aTeamRev;

    const aRepCount = a.rep_count || 0;
    const bRepCount = b.rep_count || 0;
    if (bRepCount !== aRepCount) return bRepCount - aRepCount;

    const aRev = parseFloat(a[revenueField]) || 0;
    const bRev = parseFloat(b[revenueField]) || 0;
    return bRev - aRev;
  });

  const overviewMetrics = useMemo(() => {
    const reps = overviewHealthData?.reps || [];
    const repCount = reps.length;

    const averageRepHealthScore =
      overviewHealthData?.summary?.average_score ??
      (repCount > 0
        ? Math.round(
            reps.reduce(
              (sum, rep) => sum + (Number(rep.health_score) || 0),
              0
            ) / repCount
          )
        : 0);

    const averageProgramCompletion =
      repCount > 0
        ? Math.round(
            reps.reduce(
              (sum, rep) => sum + getProgramCompletionPercent(rep),
              0
            ) / repCount
          )
        : 0;

    const averageWatchTimeMinutes =
      repCount > 0
        ? Math.round(
            reps.reduce(
              (sum, rep) => sum + (Number(rep.training_minutes) || 0),
              0
            ) / repCount
          )
        : 0;

    const teamMeetingAttendancePercentage = (() => {
      const repsWithAssignedMeetings = reps.filter(
        rep => (Number(rep.team_meetings_assigned) || 0) > 0
      );

      if (repsWithAssignedMeetings.length === 0) return null;

      const avgAttendance =
        repsWithAssignedMeetings.reduce((sum, rep) => {
          const pct = Number(rep.team_meeting_attendance_percentage);
          return sum + (Number.isFinite(pct) ? pct : 0);
        }, 0) / repsWithAssignedMeetings.length;

      return Math.round(avgAttendance);
    })();

    const repsWithNoPersonalMeetingScheduled = reps.filter(
      rep => !rep.last_manager_meeting_at
    ).length;

    return {
      averageRepHealthScore,
      averageProgramCompletion,
      averageWatchTimeMinutes,
      teamMeetingAttendancePercentage,
      repsWithNoPersonalMeetingScheduled,
    };
  }, [overviewHealthData]);

  const overviewShiftMetrics = useMemo(() => {
    const current = overviewComparison.current;
    const previous = overviewComparison.previous;

    const pctShift = (currentValue, previousValue) => {
      if (
        currentValue === null ||
        currentValue === undefined ||
        previousValue === null ||
        previousValue === undefined
      ) {
        return null;
      }

      const currentNum = Number(currentValue) || 0;
      const previousNum = Number(previousValue) || 0;

      if (previousNum === 0) {
        if (currentNum === 0) return 0;
        return null;
      }

      return ((currentNum - previousNum) / Math.abs(previousNum)) * 100;
    };

    if (!current || !previous) {
      return {
        activeReps: null,
        averageRepHealthScore: null,
        averageProgramCompletion: null,
        averageWatchTimeMinutes: null,
        projectedRevenue: null,
        teamMeetingAttendancePercentage: null,
      };
    }

    return {
      activeReps: pctShift(current.activeReps, previous.activeReps),
      averageRepHealthScore: pctShift(
        current.averageRepHealthScore,
        previous.averageRepHealthScore
      ),
      averageProgramCompletion: pctShift(
        current.averageProgramCompletion,
        previous.averageProgramCompletion
      ),
      averageWatchTimeMinutes: pctShift(
        current.averageWatchTimeMinutes,
        previous.averageWatchTimeMinutes
      ),
      projectedRevenue: pctShift(
        current.projectedRevenue,
        previous.projectedRevenue
      ),
      teamMeetingAttendancePercentage: pctShift(
        current.teamMeetingAttendancePercentage,
        previous.teamMeetingAttendancePercentage
      ),
    };
  }, [overviewComparison]);

  const formatShiftValue = shift => {
    if (shift === null || shift === undefined || Number.isNaN(shift))
      return "—";
    const rounded = Math.round(shift * 10) / 10;
    const prefix = rounded > 0 ? "+" : "";
    return `${prefix}${rounded}%`;
  };

  const getRepProfileImage = rep =>
    rep?.profile_image_url ||
    rep?.profile_image ||
    rep?.avatar_url ||
    undefined;

  const getRepDisplayName = rep =>
    rep?.rep_name ||
    `${rep?.first_name || ""} ${rep?.last_name || ""}`.trim() ||
    "Rep";

  const getRepInitials = rep =>
    getRepDisplayName(rep)
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0).toUpperCase())
      .join("") || "R";

  // Adaptive fixed card height: keeps internal scrolling, but scales to viewport
  // so the overview section is less likely to force page scrolling.
  const OVERVIEW_BOTTOM_CARD_HEIGHT =
    "clamp(470px, calc(100vh - 220px), 650px)";

  // Handle card click to filter reps
  const handleCardClick = type => {
    // Toggle filter: if clicking the same card, clear filter; otherwise set new filter
    setFilterType(filterType === type ? null : type);
  };

  // Handle season chip toggle
  const handleSeasonToggle = seasonId => {
    setSelectedSeasonIds(prev => {
      if (prev.includes(seasonId)) {
        return prev.filter(id => id !== seasonId);
      } else {
        return [...prev, seasonId];
      }
    });
  };

  // Handle select all seasons
  const handleSelectAllSeasons = () => {
    const allSeasonIds = seasons.map(season => season.id);
    setSelectedSeasonIds(allSeasonIds);
  };

  // Handle deselect all seasons
  const handleDeselectAllSeasons = () => {
    setSelectedSeasonIds([]);
  };

  // Handle Pay Conditions modal open
  const handleOpenPayConditionsModal = () => {
    setTempIncentiveCost(incentiveCostPercent);
    setTempBufferCost(bufferCostPercent);
    setIsPayConditionsModalOpen(true);
  };

  // Handle Pay Conditions save
  const handleSavePayConditions = async () => {
    try {
      setIsSavingPayConditions(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        handleAuthError("Invalid jwt");
        return;
      }

      const response = await fetch(
        getApiEndpoint("/v1/manager/pay-conditions"),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            incentive_cost_percent: tempIncentiveCost,
            buffer_cost_percent: tempBufferCost,
          }),
        }
      );

      if (response.ok) {
        setIncentiveCostPercent(tempIncentiveCost);
        setBufferCostPercent(tempBufferCost);
        setIsPayConditionsModalOpen(false);
        showSuccess("Pay conditions updated successfully!");
      } else {
        const data = await response.json().catch(() => ({}));
        showError(data?.message || "Failed to update pay conditions");
      }
    } catch (err) {
      handleGenericError(err, "Failed to update pay conditions");
    } finally {
      setIsSavingPayConditions(false);
    }
  };

  // Calculate adjusted margin and earnings
  // Cost adjustments should only apply to direct reports without marketing deals
  const calculateAdjustedEarnings = () => {
    const totalCostPercent = incentiveCostPercent + bufferCostPercent;

    // If no cost adjustments, return original values
    if (totalCostPercent === 0) {
      return {
        adjustedMargin: averageProjectedMargin,
        adjustedEarnings: projectedEarnings,
        totalCostPercent: 0,
      };
    }

    // Get all reps (flattened from hierarchy)
    const allReps = [];
    const flattenReps = repsArray => {
      repsArray.forEach(rep => {
        allReps.push(rep);
        if (rep.reps && rep.reps.length > 0) {
          flattenReps(rep.reps);
        }
      });
    };
    flattenReps(reps);

    // Separate reps into:
    // 1. Direct reports without MD (costs apply)
    // 2. All others (no cost adjustment)
    const directReports = reps; // First level of reps array = direct reports
    const qualifyingReps = directReports.filter(rep => !rep.has_md);
    const nonQualifyingReps = allReps.filter(rep => {
      // Include reps with MD OR reps that are not direct reports
      const isDirectReport = directReports.some(
        dr => dr.user_season_id === rep.user_season_id
      );
      return rep.has_md || !isDirectReport;
    });

    // Calculate earnings for qualifying reps (with cost adjustment)
    const qualifyingEarnings = qualifyingReps.reduce((sum, rep) => {
      const repEarnings =
        ((rep.projected_revenue || 0) * (rep.projected_margin || 0)) / 100;
      return sum + repEarnings;
    }, 0);

    // Calculate earnings for non-qualifying reps (no adjustment)
    const nonQualifyingEarnings = nonQualifyingReps.reduce((sum, rep) => {
      const repEarnings =
        ((rep.projected_revenue || 0) * (rep.projected_margin || 0)) / 100;
      return sum + repEarnings;
    }, 0);

    // Apply cost adjustment to qualifying earnings
    const qualifyingMarginReduction = totalCostPercent / 100;
    const adjustedQualifyingEarnings =
      qualifyingEarnings * (1 - qualifyingMarginReduction);

    // Total adjusted earnings
    const totalAdjustedEarnings =
      adjustedQualifyingEarnings + nonQualifyingEarnings;

    // Calculate effective adjusted margin
    const totalRevenue = allReps.reduce(
      (sum, rep) => sum + (rep.projected_revenue || 0),
      0
    );
    const effectiveMargin =
      totalRevenue > 0 ? (totalAdjustedEarnings / totalRevenue) * 100 : 0;

    return {
      adjustedMargin: effectiveMargin,
      adjustedEarnings: totalAdjustedEarnings,
      totalCostPercent,
    };
  };

  const { adjustedMargin, adjustedEarnings } = calculateAdjustedEarnings();

  // Fetch pay conditions from API
  const fetchPayConditions = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint("/v1/manager/pay-conditions"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setIncentiveCostPercent(result.data.incentive_cost_percent || 0);
          setBufferCostPercent(result.data.buffer_cost_percent || 0);
        }
      }
    } catch (err) {
      // Silently fail - defaults to 0
    }
  };

  const fetchOverviewMetrics = async (period = overviewPeriod) => {
    try {
      setOverviewLoading(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) return;

      const periodDays = OVERVIEW_PERIOD_DAYS[period] || 30;
      const previousPeriodDays = periodDays * 2;

      const [
        healthRes,
        previousHealthRes,
        attentionHealthRes,
        tasksRes,
        pitchesRes,
      ] = await Promise.all([
        fetch(getApiEndpoint(`/v1/rep/rep-health?days=${periodDays}`), {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
        fetch(getApiEndpoint(`/v1/rep/rep-health?days=${previousPeriodDays}`), {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
        fetch(getApiEndpoint("/v1/rep/rep-health?days=7"), {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
        fetch(getApiEndpoint("/v1/programs/tasks/pending-approvals"), {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
        fetch(getApiEndpoint("/v1/pitch-review/manager/submissions"), {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
      ]);

      let currentHealth = null;
      let previousHealth = null;

      if (healthRes.ok) {
        const healthData = await healthRes.json();
        currentHealth = healthData.data || null;
        setOverviewHealthData(currentHealth);
      }

      if (previousHealthRes.ok) {
        const previousHealthData = await previousHealthRes.json();
        previousHealth = previousHealthData.data || null;
      }

      if (attentionHealthRes.ok) {
        const attentionHealthData = await attentionHealthRes.json();
        setOverviewAttentionData(attentionHealthData.data || null);
      }

      const buildOverviewSnapshot = healthPayload => {
        const reps = healthPayload?.reps || [];
        const repCount = reps.length;
        const revenueValues = reps
          .map(rep => Number(rep.projected_revenue))
          .filter(val => Number.isFinite(val));

        const averageRepHealthScore =
          healthPayload?.summary?.average_score ??
          (repCount > 0
            ? Math.round(
                reps.reduce(
                  (sum, rep) => sum + (Number(rep.health_score) || 0),
                  0
                ) / repCount
              )
            : 0);

        const averageProgramCompletion =
          repCount > 0
            ? Math.round(
                reps.reduce(
                  (sum, rep) => sum + getProgramCompletionPercent(rep),
                  0
                ) / repCount
              )
            : 0;

        const averageWatchTimeMinutes =
          repCount > 0
            ? Math.round(
                reps.reduce(
                  (sum, rep) => sum + (Number(rep.training_minutes) || 0),
                  0
                ) / repCount
              )
            : 0;

        const teamMeetingAttendancePercentage =
          repCount > 0
            ? Math.round(
                (reps.filter(
                  rep => (Number(rep.team_meetings_attended) || 0) > 0
                ).length /
                  repCount) *
                  100
              )
            : 0;

        return {
          activeReps: repCount,
          averageRepHealthScore,
          averageProgramCompletion,
          averageWatchTimeMinutes,
          projectedRevenue:
            revenueValues.length > 0
              ? revenueValues.reduce((sum, val) => sum + val, 0)
              : null,
          teamMeetingAttendancePercentage,
        };
      };

      setOverviewComparison({
        current: buildOverviewSnapshot(currentHealth),
        previous: buildOverviewSnapshot(previousHealth),
      });

      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        const tasks = tasksData.data || [];
        setOverviewPendingTasks(tasks.length);
        setOverviewTasksList(tasks);
      }

      if (pitchesRes.ok) {
        const pitchesData = await pitchesRes.json();
        const allPitches = pitchesData.data || [];
        const pendingPitches = allPitches.filter(
          pitch => !pitch.approval_status || pitch.approval_status === "pending"
        );
        setOverviewPendingPitches(pendingPitches.length);
        setOverviewPitchesList(pendingPitches);
      }
    } catch {
      // Silent fail, overview cards fallback to current values.
    } finally {
      setOverviewLoading(false);
    }
  };

  // ── Helper: build programs/reps structures from reps-progress payload ──────
  const buildProgramStructures = repsData => {
    const programsMap = new Map();
    const programRepsMap = new Map();

    repsData.forEach(repData => {
      if (repData.programs && Array.isArray(repData.programs)) {
        repData.programs.forEach(program => {
          const programId = program.program_id || program.id;

          if (!programsMap.has(programId)) {
            programsMap.set(programId, {
              id: programId,
              program_name: program.program_name,
              cover_image: program.cover_image,
              enrolled_reps: 0,
              total_tasks: program.total_tasks || 0,
              completed_tasks: 0,
              not_started_reps: 0,
              in_progress_reps: 0,
              completed_reps: 0,
              pending_approvals: 0,
              completion_percentage: 0,
            });
            programRepsMap.set(programId, []);
          }

          const programEntry = programsMap.get(programId);
          programEntry.enrolled_reps++;
          const repCompletedTasks = program.completed_tasks || 0;
          programEntry.completed_tasks += repCompletedTasks;

          const totalTasks = program.total_tasks || 0;
          let repStatus = "not_started";
          if (repCompletedTasks === 0) {
            programEntry.not_started_reps++;
          } else if (totalTasks > 0 && repCompletedTasks >= totalTasks) {
            programEntry.completed_reps++;
            repStatus = "completed";
          } else {
            programEntry.in_progress_reps++;
            repStatus = "in_progress";
          }

          programRepsMap.get(programId).push({
            rep_name:
              repData.rep_name ||
              repData.user_name ||
              repData.name ||
              "Unknown",
            user_season_id: repData.rep_id || repData.user_season_id || null,
            completed_tasks: repCompletedTasks,
            total_tasks: totalTasks,
            status: repStatus,
            last_activity_at: program.last_activity_at || null,
          });
        });
      }
    });

    const programs = Array.from(programsMap.values()).map(program => ({
      ...program,
      completion_percentage:
        program.total_tasks > 0
          ? Math.round(
              (program.completed_tasks /
                (program.total_tasks * program.enrolled_reps)) *
                100
            )
          : 0,
    }));

    return { programs, programReps: Object.fromEntries(programRepsMap) };
  };

  // ── Helper: compute KPI metrics from programs/reps structures ─────────────
  const computeFastKpis = (programs, programReps) => {
    const totalEnrolled = programs.reduce(
      (sum, p) => sum + (p.enrolled_reps || 0),
      0
    );
    const totalCompleted = programs.reduce(
      (sum, p) => sum + (p.completed_reps || 0),
      0
    );
    const completionRate =
      totalEnrolled > 0
        ? Math.round((totalCompleted / totalEnrolled) * 100)
        : 0;

    const repIndex = new Map();
    Object.values(programReps).forEach(reps => {
      reps.forEach(rep => {
        const key = rep.user_season_id;
        if (!key) return;
        if (!repIndex.has(key)) {
          repIndex.set(key, {
            lastActivityAt: rep.last_activity_at,
            hasNotStarted: rep.status === "not_started",
          });
        } else {
          const existing = repIndex.get(key);
          const existingTime = existing.lastActivityAt
            ? new Date(existing.lastActivityAt).getTime()
            : null;
          const incomingTime = rep.last_activity_at
            ? new Date(rep.last_activity_at).getTime()
            : null;
          if (
            incomingTime !== null &&
            (existingTime === null || incomingTime > existingTime)
          ) {
            existing.lastActivityAt = rep.last_activity_at;
          }
          if (rep.status === "not_started") existing.hasNotStarted = true;
        }
      });
    });

    const nowMs = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    const fourteenDaysMs = 14 * 24 * 60 * 60 * 1000;
    let activeLearners7d = 0;
    let atRiskReps = 0;

    repIndex.forEach(rep => {
      const lastMs = rep.lastActivityAt
        ? new Date(rep.lastActivityAt).getTime()
        : null;
      const inactivityMs =
        lastMs === null ? Number.POSITIVE_INFINITY : nowMs - lastMs;
      if (inactivityMs <= sevenDaysMs) activeLearners7d += 1;
      if (rep.hasNotStarted || inactivityMs >= fourteenDaysMs) atRiskReps += 1;
    });

    return { completionRate, activeLearners7d, atRiskReps, totalCompleted };
  };

  // ── Fetch program data from API (two-phase: fast table data, then slow KPIs) ─
  const fetchProgramData = async () => {
    try {
      setProgramLoading(true);
      setProgramError(null);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setProgramError("Authentication failed");
        return;
      }

      // ── Phase 1: fast — reps-progress + pending-approvals ─────────────────
      const [statsResponse, approvalsResponse] = await Promise.all([
        fetch(getApiEndpoint("/v1/programs/reps-progress"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
        fetch(getApiEndpoint("/v1/programs/tasks/pending-approvals"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
      ]);

      if (!statsResponse.ok || !approvalsResponse.ok) {
        setProgramError("Failed to fetch program data");
        return;
      }

      const statsResult = await statsResponse.json();
      const approvalsResult = await approvalsResponse.json();

      const repsData = statsResult.data || [];
      const { programs, programReps } = buildProgramStructures(repsData);

      // Get pending approvals
      const pendingApprovals = (approvalsResult.data || []).map(approval => ({
        completion_id: approval.completion_id,
        task_id: approval.task_id,
        task_name: approval.task_name,
        task_desc: approval.task_desc,
        task_type: approval.task_type,
        program_name: approval.program_name,
        program_id: approval.program_id,
        rep_name: approval.user_name,
        user_season_id: approval.user_id,
        text_response: approval.text_response,
        submitted_at: approval.submitted_at,
        files: approval.files || [],
      }));

      // Count pending approvals per program
      pendingApprovals.forEach(approval => {
        const programEntry = programs.find(p => p.id === approval.program_id);
        if (programEntry) programEntry.pending_approvals++;
      });

      // Compute fast KPIs (no per-program API calls needed)
      const { completionRate, activeLearners7d, atRiskReps, totalCompleted } =
        computeFastKpis(programs, programReps);

      // Render table immediately with avgDaysToCompleteProgram still null (loading)
      setProgramTrackingMetrics({
        completionRate,
        activeLearners7d,
        atRiskReps,
        avgDaysToCompleteProgram: null,
      });
      setProgramData({
        pending_approvals: pendingApprovals,
        programs,
        program_reps: programReps,
      });

      // Remove spinner — table is ready
      setProgramLoading(false);

      // ── Phase 2: slow (background) — per-program progress for avg days ────
      setProgramAvgDaysLoading(true);
      try {
        const completionDurationsDays = [];
        await Promise.all(
          programs.map(async program => {
            try {
              const res = await fetch(
                getApiEndpoint(`/v1/programs/progress/${program.id}`),
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${jwt}`,
                  },
                }
              );
              if (!res.ok) return;
              const details = await res.json();
              (details?.data?.assigned_users || []).forEach(userProgress => {
                const totalTasks = userProgress?.total_tasks_count || 0;
                const completedCount = userProgress?.completed_tasks_count || 0;
                if (
                  !userProgress?.is_completed ||
                  totalTasks === 0 ||
                  completedCount < totalTasks
                ) {
                  return;
                }

                const dates = (userProgress.tasks || [])
                  .filter(t => t?.is_completed && t?.completed_at)
                  .map(t => new Date(t.completed_at))
                  .filter(d => !Number.isNaN(d.getTime()))
                  .sort((a, b) => a.getTime() - b.getTime());

                if (dates.length < 2) return;
                const diffDays =
                  (dates[dates.length - 1].getTime() - dates[0].getTime()) /
                  (1000 * 60 * 60 * 24);
                if (diffDays > 0) completionDurationsDays.push(diffDays);
              });
            } catch {
              // Keep metrics resilient if one program request fails.
            }
          })
        );

        const avgDaysToCompleteProgram =
          totalCompleted > 0 && completionDurationsDays.length > 0
            ? Math.round(
                (completionDurationsDays.reduce((s, d) => s + d, 0) /
                  completionDurationsDays.length) *
                  10
              ) / 10
            : null;

        setProgramTrackingMetrics(prev => ({
          ...prev,
          avgDaysToCompleteProgram,
        }));
      } catch {
        // Avg-days failed silently — other KPIs still visible
      } finally {
        setProgramAvgDaysLoading(false);
      }
    } catch (err) {
      handleGenericError(err, "Program error");
      setProgramError("Failed to fetch program data");
      setProgramTrackingMetrics({
        completionRate: 0,
        activeLearners7d: 0,
        atRiskReps: 0,
        avgDaysToCompleteProgram: null,
      });
      setProgramLoading(false);
      setProgramAvgDaysLoading(false);
    }
  };

  // Silent background refresh — does NOT flip programLoading so the UI never blanks out
  const silentRefreshProgramData = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const [statsResponse, approvalsResponse] = await Promise.all([
        fetch(getApiEndpoint("/v1/programs/reps-progress"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
        fetch(getApiEndpoint("/v1/programs/tasks/pending-approvals"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
      ]);

      if (!statsResponse.ok || !approvalsResponse.ok) return;

      const statsResult = await statsResponse.json();
      const approvalsResult = await approvalsResponse.json();

      const repsData = statsResult.data || [];
      const programsMap = new Map();

      repsData.forEach(repData => {
        if (repData.programs && Array.isArray(repData.programs)) {
          repData.programs.forEach(program => {
            const programId = program.program_id || program.id;
            if (!programsMap.has(programId)) {
              programsMap.set(programId, {
                id: programId,
                program_name: program.program_name,
                cover_image: program.cover_image,
                enrolled_reps: 0,
                total_tasks: program.total_tasks || 0,
                completed_tasks: 0,
                not_started_reps: 0,
                in_progress_reps: 0,
                completed_reps: 0,
                pending_approvals: 0,
                completion_percentage: 0,
              });
            }
            const programEntry = programsMap.get(programId);
            programEntry.enrolled_reps++;
            programEntry.completed_tasks += program.completed_tasks || 0;
          });
        }
      });

      const pendingApprovals = (approvalsResult.data || []).map(approval => ({
        completion_id: approval.completion_id,
        task_id: approval.task_id,
        task_name: approval.task_name,
        task_desc: approval.task_desc,
        task_type: approval.task_type,
        program_name: approval.program_name,
        program_id: approval.program_id,
        rep_name: approval.user_name,
        user_season_id: approval.user_id,
        text_response: approval.text_response,
        submitted_at: approval.submitted_at,
        files: approval.files || [],
      }));

      pendingApprovals.forEach(approval => {
        const programEntry = programsMap.get(approval.program_id);
        if (programEntry) {
          programEntry.pending_approvals++;
        }
      });

      const programs = Array.from(programsMap.values()).map(program => ({
        ...program,
        completion_percentage:
          program.total_tasks > 0
            ? Math.round(
                (program.completed_tasks /
                  (program.total_tasks * program.enrolled_reps)) *
                  100
              )
            : 0,
      }));

      setProgramData({
        pending_approvals: pendingApprovals,
        programs: programs,
      });
    } catch {
      // Silent fail — the optimistic update already reflects the correct state
    }
  };

  // Handle approve task
  const handleApproveTask = async (approval, feedback) => {
    // 1. Optimistically remove the item from the list and close the modal instantly
    setProgramData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        pending_approvals: prev.pending_approvals.filter(
          a => a.completion_id !== approval.completion_id
        ),
        programs: prev.programs.map(p =>
          p.id === approval.program_id
            ? {
                ...p,
                pending_approvals: Math.max(0, (p.pending_approvals || 0) - 1),
              }
            : p
        ),
      };
    });
    setShowApprovalDetailModal(false);
    setSelectedApproval(null);

    // 2. Fire the API request in the background
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        showError("Authentication failed");
        fetchProgramData(); // restore on auth failure
        return;
      }

      const response = await fetch(
        getApiEndpoint("/v1/programs/tasks/approve"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            completion_id: approval.completion_id,
            feedback: feedback || undefined,
          }),
        }
      );

      if (response.ok) {
        showSuccess("Task approved successfully!");
        // Silent reconcile — no loading flash
        silentRefreshProgramData();
      } else {
        showError("Failed to approve task");
        fetchProgramData(); // restore correct state on failure
      }
    } catch (err) {
      handleGenericError(err, "Failed to approve task");
      fetchProgramData(); // restore correct state on error
    }
  };

  // Handle reject task
  const handleRejectTask = async (approval, feedback) => {
    // 1. Optimistically remove the item from the list and close the modal instantly
    setProgramData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        pending_approvals: prev.pending_approvals.filter(
          a => a.completion_id !== approval.completion_id
        ),
        programs: prev.programs.map(p =>
          p.id === approval.program_id
            ? {
                ...p,
                pending_approvals: Math.max(0, (p.pending_approvals || 0) - 1),
              }
            : p
        ),
      };
    });
    setShowApprovalDetailModal(false);
    setSelectedApproval(null);

    // 2. Fire the API request in the background
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        showError("Authentication failed");
        fetchProgramData(); // restore on auth failure
        return;
      }

      const response = await fetch(
        getApiEndpoint("/v1/programs/tasks/reject"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            completion_id: approval.completion_id,
            feedback: feedback || undefined,
          }),
        }
      );

      if (response.ok) {
        showSuccess("Task rejected - rep can resubmit");
        // Silent reconcile — no loading flash
        silentRefreshProgramData();
      } else {
        showError("Failed to reject task");
        fetchProgramData(); // restore correct state on failure
      }
    } catch (err) {
      handleGenericError(err, "Failed to reject task");
      fetchProgramData(); // restore correct state on error
    }
  };

  // Fetch detailed task progress for a single rep in a program
  const fetchRepDetail = async (programId, userSeasonId) => {
    setRepDetailLoading(true);
    setRepDetailData(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(
        getApiEndpoint(`/v1/programs/progress/${programId}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (response.ok) {
        const result = await response.json();
        const users = result.data?.assigned_users || [];
        const user = users.find(u => u.user_season_id === userSeasonId);
        setRepDetailData(user || null);
      }
    } catch {
      // silent fail — modal shows empty state
    } finally {
      setRepDetailLoading(false);
    }
  };

  // Handle drag-to-reschedule an existing event
  const handleEventDrop = ({ event, start, end }) => {
    if (event.isDraft || event.id == null) return; // never drag the draft ghost
    const durationMs =
      (event.end ? event.end.getTime() : event.start.getTime() + 3600000) -
      event.start.getTime();
    const newStart = new Date(start);
    const newEnd = end
      ? new Date(end)
      : new Date(newStart.getTime() + durationMs);

    // Optimistic update — move just this event, keep all others in place
    setCalendarEvents(prev =>
      prev.map(e =>
        e.id === event.id ? { ...e, start: newStart, end: newEnd } : e
      )
    );

    // Fire-and-forget PATCH; do NOT revert on failure to avoid all-events snap-back
    getVerifiedJWT().then(jwt => {
      fetch(getApiEndpoint(`/v1/attendance/team-events/${event.id}`), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ scheduled_time: newStart.toISOString() }),
      })
        .then(async response => {
          if (!response.ok) return;
          // Re-fetch after the optimistic move so grouped meetings, titles,
          // and any attendance-linked views stay in sync with the backend.
          await fetchTeamEvents();
          await fetchAttendanceEvents();
        })
        .catch(() => {
          // silent – position already updated visually
        });
    });
  };

  // Handle tab change
  const handleTabChange = tabId => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Handle view details
  const handleViewDetails = approval => {
    setSelectedApproval(approval);
    setShowApprovalDetailModal(true);
  };

  // Set active tab from URL parameter on mount
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && TABS.some(t => t.id === tabParam)) {
      setActiveTab(tabParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch pay conditions once on mount (needed for revenue tab calculations)
  useEffect(() => {
    fetchPayConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === "overview") {
      fetchOverviewMetrics(overviewPeriod);
    } else if (activeTab === "my-downline") {
      fetchSeasons();
      fetchReps();
    } else if (activeTab === "programs" && !programData) {
      fetchProgramData();
    } else if (activeTab === "events") {
      fetchTeamEvents();
      fetchAttendanceEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Re-fetch reps when selected seasons change
  useEffect(() => {
    if (activeTab === "my-downline" && seasons.length > 0) {
      fetchReps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeasonIds]);

  // Re-fetch overview metrics when period changes
  useEffect(() => {
    if (activeTab === "overview") {
      fetchOverviewMetrics(overviewPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overviewPeriod]);

  // If viewing a rep detail, show the detail view
  if (viewingRepDetail) {
    return (
      <PageContent title="Downline Management">
        <RepDetailView
          rep={viewingRepDetail}
          onBack={() => setViewingRepDetail(null)}
        />
      </PageContent>
    );
  }

  return (
    <PageContent
      customHeader={
        <div className="org-page-custom-header">
          <div className="org-page-header-left">
            <h1 className="org-page-main-title">Downline Management</h1>
            <nav className="org-page-nav-tabs" role="tablist">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    className={`org-page-nav-tab${activeTab === tab.id ? " active" : ""}`}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {activeTab === "overview" && (
            <div className="org-page-header-right">
              <Menu shadow="md" width={180} position="bottom-end">
                <Menu.Target>
                  <Button
                    radius="xl"
                    color="gray"
                    variant="light"
                    size="sm"
                    rightSection={<IconChevronDown size={14} />}
                    style={{
                      border: "1px solid #d0d5dd",
                      color: "#344054",
                      background: "#ffffff",
                    }}
                  >
                    {`Time: ${getOverviewPeriodLabel(overviewPeriod)}`}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown
                  style={{ border: "1px solid #e4e7ec", background: "#fff" }}
                >
                  {["day", "week", "month", "quarter", "year"].map(period => (
                    <Menu.Item
                      key={period}
                      onClick={() => setOverviewPeriod(period)}
                      leftSection={
                        overviewPeriod === period ? (
                          <IconCheck size={14} />
                        ) : null
                      }
                    >
                      {getOverviewPeriodLabel(period)}
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </div>
          )}
        </div>
      }
    >
      {activeTab === "overview" && (
        <Stack gap="xl">
          {/* ── Stat cards — all 6 in one row ── */}
          <Grid gutter="xs">
            {[
              {
                label: "Total Active Reps",
                value: `${totalActiveReps}/${totalReps}`,
                icon: IconUsers,
                iconColor: "#228be6",
                iconBg: "#e7f5ff",
              },
              {
                label: "Average Rep Health",
                value: `${overviewMetrics.averageRepHealthScore}%`,
                icon: IconHeart,
                iconColor: "#e03131",
                iconBg: "#fff5f5",
              },
              {
                label: "Program Completion",
                value: `${overviewMetrics.averageProgramCompletion}%`,
                icon: IconTargetArrow,
                iconColor: "#7048e8",
                iconBg: "#f3f0ff",
              },
              {
                label: "Average Watch Time",
                value: `${(overviewMetrics.averageWatchTimeMinutes / 60).toFixed(1)} hrs`,
                icon: IconClock,
                iconColor: "#0ca678",
                iconBg: "#e6fcf5",
              },
              {
                label: "Projected Revenue",
                value: (() => {
                  const n = parseFloat(projectedRevenue) || 0;
                  return n >= 1_000_000
                    ? `$${(n / 1_000_000).toFixed(1)}M`
                    : n >= 1_000
                      ? `$${(n / 1_000).toFixed(1)}K`
                      : formatCurrency(n);
                })(),
                icon: IconCurrencyDollar,
                iconColor: "#2f9e44",
                iconBg: "#ebfbee",
              },
              {
                label: "Meeting Attendance",
                value:
                  overviewMetrics.teamMeetingAttendancePercentage === null
                    ? "—"
                    : `${overviewMetrics.teamMeetingAttendancePercentage}%`,
                icon: IconCalendarEvent,
                iconColor: "#f59f00",
                iconBg: "#fff9db",
              },
            ].map(({ label, value, icon: Icon, iconColor, iconBg }) => {
              const shiftMap = {
                "Total Active Reps": overviewShiftMetrics.activeReps,
                "Average Rep Health":
                  overviewShiftMetrics.averageRepHealthScore,
                "Program Completion":
                  overviewShiftMetrics.averageProgramCompletion,
                "Average Watch Time":
                  overviewShiftMetrics.averageWatchTimeMinutes,
                "Projected Revenue": overviewShiftMetrics.projectedRevenue,
                "Meeting Attendance":
                  overviewShiftMetrics.teamMeetingAttendancePercentage,
              };

              const shiftValue = shiftMap[label];

              return (
                <Grid.Col key={label} span={{ base: 12, sm: 6, md: 4, lg: 2 }}>
                  <Card
                    padding="xl"
                    radius="lg"
                    withBorder
                    h="100%"
                    style={{ border: "1px solid #e9ecef" }}
                  >
                    <Stack
                      gap="lg"
                      align="center"
                      justify="space-between"
                      h="100%"
                      style={{ textAlign: "center" }}
                    >
                      <Box
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          background: iconBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto",
                        }}
                      >
                        <Icon size={18} color={iconColor} />
                      </Box>
                      <Text
                        size="md"
                        c="dimmed"
                        fw={600}
                        ta="center"
                        lineClamp={1}
                      >
                        {label}
                      </Text>
                      <Text
                        fw={800}
                        ta="center"
                        style={{ fontSize: 20, lineHeight: 1.05 }}
                      >
                        {value}
                      </Text>
                      <Text size="xs" c="dimmed" ta="center">
                        {formatShiftValue(shiftValue)}{" "}
                        {OVERVIEW_COMPARISON_LABELS[overviewPeriod] ||
                          "vs last period"}
                      </Text>
                    </Stack>
                  </Card>
                </Grid.Col>
              );
            })}
          </Grid>

          {/* ── Bottom panels ── */}
          <Grid gutter="xs" align="stretch" style={{}}>
            {/* Tasks Waiting Approval */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Card
                padding="xl"
                radius="lg"
                withBorder
                style={{
                  border: "1px solid #e9ecef",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  height: OVERVIEW_BOTTOM_CARD_HEIGHT,
                  minHeight: OVERVIEW_BOTTOM_CARD_HEIGHT,
                  maxHeight: OVERVIEW_BOTTOM_CARD_HEIGHT,
                }}
              >
                <Group justify="space-between" mb="lg">
                  <Text fw={700} size="lg">
                    Tasks Waiting Approval
                  </Text>
                  {overviewPendingTasks > 0 && (
                    <Box
                      style={{
                        background: "#d0521f",
                        color: "#fff",
                        borderRadius: "50%",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {overviewPendingTasks}
                    </Box>
                  )}
                </Group>
                <Stack
                  gap="sm"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {overviewLoading ? (
                    <Group justify="center" py="md">
                      <Loader size="sm" />
                    </Group>
                  ) : overviewTasksList.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      No tasks waiting for approval
                    </Text>
                  ) : (
                    overviewTasksList.map((task, i) => {
                      const submittedMs = task.submitted_at
                        ? Date.now() - new Date(task.submitted_at).getTime()
                        : null;
                      const timeAgo = submittedMs
                        ? submittedMs < 3_600_000
                          ? `${Math.round(submittedMs / 60_000)} minutes ago`
                          : submittedMs < 86_400_000
                            ? `${Math.round(submittedMs / 3_600_000)} hour${Math.round(submittedMs / 3_600_000) !== 1 ? "s" : ""} ago`
                            : `${Math.round(submittedMs / 86_400_000)} day${Math.round(submittedMs / 86_400_000) !== 1 ? "s" : ""} ago`
                        : "";
                      return (
                        <Box
                          key={i}
                          style={{
                            padding: "12px 14px",
                            borderRadius: 10,
                            border: "1px solid #f1f3f5",
                            background: "#fafafa",
                          }}
                        >
                          <Group
                            justify="space-between"
                            align="flex-start"
                            wrap="nowrap"
                            gap="xs"
                          >
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text fw={700} size="sm" lineClamp={1}>
                                {task.task_name}
                              </Text>
                              <Group gap={6} mt={4} align="center">
                                <Box
                                  style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: "50%",
                                    background: "#e9ecef",
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <IconUsers size={11} color="#868e96" />
                                </Box>
                                <Text size="xs" c="dimmed" lineClamp={1}>
                                  {task.user_name || task.rep_name}
                                </Text>
                              </Group>
                              {timeAgo && (
                                <Text size="xs" c="dimmed" mt={4} ta="right">
                                  {timeAgo}
                                </Text>
                              )}
                            </Box>
                            <Button
                              size="xs"
                              color="gray"
                              variant="light"
                              radius="md"
                              leftSection={<IconFileText size={12} />}
                              style={{ flexShrink: 0 }}
                              onClick={() => {
                                handleTabChange("programs");
                              }}
                            >
                              Review
                            </Button>
                          </Group>
                        </Box>
                      );
                    })
                  )}
                </Stack>
              </Card>
            </Grid.Col>

            {/* Pitch Reviews */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Card
                padding="xl"
                radius="lg"
                withBorder
                style={{
                  border: "1px solid #e9ecef",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  height: OVERVIEW_BOTTOM_CARD_HEIGHT,
                  minHeight: OVERVIEW_BOTTOM_CARD_HEIGHT,
                  maxHeight: OVERVIEW_BOTTOM_CARD_HEIGHT,
                }}
              >
                <Group justify="space-between" mb="lg">
                  <Text fw={700} size="lg">
                    Pitch Reviews
                  </Text>
                  {overviewPendingPitches > 0 && (
                    <Box
                      style={{
                        background: "#d0521f",
                        color: "#fff",
                        borderRadius: "50%",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {overviewPendingPitches}
                    </Box>
                  )}
                </Group>
                <Stack
                  gap="sm"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {overviewLoading ? (
                    <Group justify="center" py="md">
                      <Loader size="sm" />
                    </Group>
                  ) : overviewPitchesList.length === 0 ? (
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      No pitches awaiting review
                    </Text>
                  ) : (
                    overviewPitchesList.map((pitch, i) => {
                      const submittedMs =
                        pitch.submitted_at || pitch.created_at
                          ? Date.now() -
                            new Date(
                              pitch.submitted_at || pitch.created_at
                            ).getTime()
                          : null;
                      const timeAgo = submittedMs
                        ? submittedMs < 3_600_000
                          ? `${Math.round(submittedMs / 60_000)} minutes ago`
                          : submittedMs < 86_400_000
                            ? `${Math.round(submittedMs / 3_600_000)} hour${Math.round(submittedMs / 3_600_000) !== 1 ? "s" : ""} ago`
                            : `${Math.round(submittedMs / 86_400_000)} day${Math.round(submittedMs / 86_400_000) !== 1 ? "s" : ""} ago`
                        : "";
                      return (
                        <Box
                          key={i}
                          style={{
                            padding: "12px 14px",
                            borderRadius: 10,
                            border: "1px solid #f1f3f5",
                            background: "#fafafa",
                          }}
                        >
                          <Group
                            justify="space-between"
                            align="flex-start"
                            wrap="nowrap"
                            gap="xs"
                          >
                            <Group
                              gap="sm"
                              wrap="nowrap"
                              style={{ flex: 1, minWidth: 0 }}
                            >
                              <Box
                                style={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 8,
                                  flexShrink: 0,
                                  background: "#fee2d5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <IconVideo size={16} color="#d0521f" />
                              </Box>
                              <Box style={{ minWidth: 0 }}>
                                <Text fw={700} size="sm" lineClamp={1}>
                                  {pitch.title ||
                                    pitch.pitch_title ||
                                    pitch.channel_name ||
                                    "Pitch Review"}
                                </Text>
                                <Group gap={6} mt={2} align="center">
                                  <Box
                                    style={{
                                      width: 18,
                                      height: 18,
                                      borderRadius: "50%",
                                      background: "#e9ecef",
                                      flexShrink: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <IconUsers size={11} color="#868e96" />
                                  </Box>
                                  <Text size="xs" c="dimmed" lineClamp={1}>
                                    {pitch.sender_name ||
                                      pitch.rep_name ||
                                      pitch.user_name ||
                                      "Rep"}
                                  </Text>
                                </Group>
                                {timeAgo && (
                                  <Text size="xs" c="dimmed" mt={2}>
                                    {timeAgo}
                                  </Text>
                                )}
                              </Box>
                            </Group>
                            <Button
                              size="xs"
                              color="gray"
                              variant="light"
                              radius="md"
                              leftSection={<IconPlayerPlay size={12} />}
                              style={{ flexShrink: 0 }}
                              onClick={() => handleTabChange("pitch-reviews")}
                            >
                              Review
                            </Button>
                          </Group>
                        </Box>
                      );
                    })
                  )}
                </Stack>
              </Card>
            </Grid.Col>

            {/* Reps Need Attention */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              {(() => {
                const sevenDaysAgoMs = Date.now() - 7 * 24 * 60 * 60 * 1000;

                const repsNeedingAttention = (overviewAttentionData?.reps || [])
                  .map(rep => {
                    const trainedInPastWeek =
                      (Number(rep.training_minutes) || 0) > 0;
                    const metManagerInPastWeek =
                      !!rep.last_manager_meeting_at &&
                      new Date(rep.last_manager_meeting_at).getTime() >=
                        sevenDaysAgoMs;
                    const attendedWeeklyMeetingInPastWeek =
                      (Number(rep.team_meetings_assigned) || 0) === 0
                        ? true
                        : !!rep.last_team_meeting_at &&
                          new Date(rep.last_team_meeting_at).getTime() >=
                            sevenDaysAgoMs;

                    const missedReasons = [
                      !trainedInPastWeek ? "No training in past week" : null,
                      !metManagerInPastWeek
                        ? "No manager meeting in past week"
                        : null,
                      !attendedWeeklyMeetingInPastWeek
                        ? "No weekly meeting in past week"
                        : null,
                    ].filter(Boolean);

                    return {
                      ...rep,
                      missedReasons,
                      missedCount: missedReasons.length,
                    };
                  })
                  .filter(rep => {
                    return rep.missedCount > 0;
                  })
                  .sort((a, b) => {
                    if (b.missedCount !== a.missedCount)
                      return b.missedCount - a.missedCount;
                    return getRepDisplayName(a).localeCompare(
                      getRepDisplayName(b)
                    );
                  });
                return (
                  <Card
                    padding="xl"
                    radius="lg"
                    withBorder
                    style={{
                      border: "1px solid #e9ecef",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      height: OVERVIEW_BOTTOM_CARD_HEIGHT,
                      minHeight: OVERVIEW_BOTTOM_CARD_HEIGHT,
                      maxHeight: OVERVIEW_BOTTOM_CARD_HEIGHT,
                    }}
                  >
                    <Group justify="space-between" mb="lg">
                      <Text fw={700} size="lg">
                        Reps Need Attention
                      </Text>
                      {repsNeedingAttention.length > 0 && (
                        <Box
                          style={{
                            background: "#d0521f",
                            color: "#fff",
                            borderRadius: "50%",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {repsNeedingAttention.length}
                        </Box>
                      )}
                    </Group>
                    <Stack
                      gap="sm"
                      style={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        paddingRight: 4,
                      }}
                    >
                      {overviewLoading ? (
                        <Group justify="center" py="md">
                          <Loader size="sm" />
                        </Group>
                      ) : repsNeedingAttention.length === 0 ? (
                        <Stack align="center" py="md" gap="xs">
                          <Box
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              background: "#d3f9d8",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconCheck size={24} color="#2f9e44" />
                          </Box>
                          <Text size="sm" c="dimmed" ta="center">
                            All reps are on track
                          </Text>
                        </Stack>
                      ) : (
                        repsNeedingAttention.map((rep, i) => {
                          const reason = rep.missedReasons.join(" • ");

                          const lastActivityMs = rep.last_activity_at
                            ? Date.now() -
                              new Date(rep.last_activity_at).getTime()
                            : null;
                          const timeAgo = lastActivityMs
                            ? lastActivityMs < 86_400_000
                              ? `${Math.round(lastActivityMs / 3_600_000)} hours ago`
                              : lastActivityMs < 604_800_000
                                ? `${Math.round(lastActivityMs / 86_400_000)} days ago`
                                : `${Math.round(lastActivityMs / 604_800_000)} week${Math.round(lastActivityMs / 604_800_000) !== 1 ? "s" : ""} ago`
                            : null;

                          return (
                            <Box
                              key={i}
                              style={{
                                padding: "12px 14px",
                                borderRadius: 10,
                                border: "1px solid #f1f3f5",
                                background: "#fafafa",
                              }}
                            >
                              <Group
                                justify="space-between"
                                align="flex-start"
                                wrap="nowrap"
                                gap="xs"
                              >
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                  <Group gap={8} align="center" wrap="nowrap">
                                    <Avatar
                                      src={getRepProfileImage(rep)}
                                      radius="xl"
                                      size="sm"
                                      color="orange"
                                    >
                                      {getRepInitials(rep)}
                                    </Avatar>
                                    <Text fw={700} size="sm" lineClamp={1}>
                                      {getRepDisplayName(rep)}
                                    </Text>
                                  </Group>
                                  <Text
                                    size="xs"
                                    c="dimmed"
                                    mt={4}
                                    lineClamp={2}
                                  >
                                    {reason}
                                  </Text>
                                  {timeAgo && (
                                    <Text
                                      size="xs"
                                      c="dimmed"
                                      mt={4}
                                      ta="right"
                                    >
                                      {timeAgo}
                                    </Text>
                                  )}
                                </Box>
                                <Button
                                  size="xs"
                                  color="gray"
                                  variant="light"
                                  radius="md"
                                  leftSection={<IconAlertCircle size={12} />}
                                  style={{ flexShrink: 0 }}
                                  onClick={() => handleTabChange("rep-health")}
                                >
                                  Contact
                                </Button>
                              </Group>
                            </Box>
                          );
                        })
                      )}
                    </Stack>
                  </Card>
                );
              })()}
            </Grid.Col>
          </Grid>
        </Stack>
      )}

      {activeTab === "my-downline" && (
        <Stack gap="lg">
          {/* Metrics Cards — with pay details on, the logged-in user's own
              figures in the mode showing (current / projected); otherwise the
              roster counts. */}
          {showPayDetails ? (
            <Grid>
              {(() => {
                const c = callerMetrics || {};
                const cur = payMode === "current";
                const pick = key => (cur ? c[key] : c[`projected_${key}`]);
                const money = v => (v == null ? "—" : formatCurrency(v));
                const pct = v => (v == null ? "—" : formatPercentage(v));
                const modeWord = cur ? "current" : "projected";
                const cardStyle = { padding: "xl", radius: "lg" };
                return (
                  <>
                    <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                      <Card {...cardStyle} withBorder h="100%">
                        <Stack gap="md" h="100%">
                          <Group gap="sm">
                            <ThemeIcon
                              size={42}
                              radius="md"
                              variant="light"
                              color="blue"
                            >
                              <IconTrendingUp size={22} />
                            </ThemeIcon>
                            <Text fw={600}>Revenue</Text>
                          </Group>
                          <Group grow align="flex-start">
                            <Box>
                              <Text size="sm" c="dimmed" fw={500} mb={4}>
                                Personal
                              </Text>
                              <Text fz="xl" fw={700}>
                                {money(pick("personal_revenue"))}
                              </Text>
                            </Box>
                            <Box>
                              <Text size="sm" c="dimmed" fw={500} mb={4}>
                                Downline
                              </Text>
                              <Text fz="xl" fw={700}>
                                {money(pick("downline_revenue"))}
                              </Text>
                            </Box>
                          </Group>
                          <Text size="sm" c="dimmed">
                            Your {modeWord} production
                          </Text>
                        </Stack>
                      </Card>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                      <Card {...cardStyle} withBorder h="100%">
                        <Stack gap="md" h="100%">
                          <Group gap="sm">
                            <ThemeIcon
                              size={42}
                              radius="md"
                              variant="light"
                              color="violet"
                            >
                              <IconChartLine size={22} />
                            </ThemeIcon>
                            <Text fw={600}>Commission</Text>
                          </Group>
                          <Group grow align="flex-start">
                            <Box>
                              <Text size="sm" c="dimmed" fw={500} mb={4}>
                                Ending commission
                              </Text>
                              <Text fz="xl" fw={700}>
                                {pct(pick("commission_percentage"))}
                              </Text>
                            </Box>
                            <Box>
                              <Text size="sm" c="dimmed" fw={500} mb={4}>
                                Personal earnings
                              </Text>
                              <Text fz="xl" fw={700}>
                                {money(pick("commission_earnings"))}
                              </Text>
                            </Box>
                          </Group>
                          <Text size="sm" c="dimmed">
                            On your personal revenue
                          </Text>
                        </Stack>
                      </Card>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                      <Card {...cardStyle} withBorder h="100%">
                        <Stack gap="md" h="100%">
                          <Group gap="sm">
                            <ThemeIcon
                              size={42}
                              radius="md"
                              variant="light"
                              color="teal"
                            >
                              <IconUsers size={22} />
                            </ThemeIcon>
                            <Text fw={600}>Downline</Text>
                          </Group>
                          <Group grow align="flex-start">
                            <Box>
                              <Text size="sm" c="dimmed" fw={500} mb={4}>
                                Ending MD
                              </Text>
                              <Text fz="xl" fw={700}>
                                {pct(pick("md_percentage"))}
                              </Text>
                            </Box>
                            <Box>
                              <Text size="sm" c="dimmed" fw={500} mb={4}>
                                Margin
                              </Text>
                              <Text fz="xl" fw={700}>
                                {pct(pick("downline_margin"))}
                              </Text>
                            </Box>
                          </Group>
                          <Box>
                            <Text size="sm" c="dimmed" fw={500} mb={4}>
                              Earnings from downline
                            </Text>
                            <Text fz="xl" fw={700}>
                              {money(pick("downline_earnings"))}
                            </Text>
                          </Box>
                        </Stack>
                      </Card>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                      <Card {...cardStyle} withBorder h="100%">
                        <Stack gap="lg" justify="space-between" h="100%">
                          <ThemeIcon
                            size={42}
                            radius="md"
                            variant="light"
                            color="green"
                          >
                            <IconCurrencyDollar size={22} />
                          </ThemeIcon>
                          <Box>
                            <Text size="sm" c="dimmed" fw={500} mb={4}>
                              Total Earnings
                            </Text>
                            <Text fz="2xl" fw={700}>
                              {money(pick("total_earnings"))}
                            </Text>
                          </Box>
                          <Text size="sm" c="dimmed">
                            Personal earnings plus earnings from downline
                          </Text>
                        </Stack>
                      </Card>
                    </Grid.Col>
                  </>
                );
              })()}
            </Grid>
          ) : (
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                <Card
                  padding="xl"
                  radius="lg"
                  withBorder
                  h="100%"
                  style={{ cursor: "pointer", transition: "all 0.2s" }}
                  onClick={() => handleCardClick(null)}
                >
                  <Stack gap="lg" justify="space-between" h="100%">
                    <ThemeIcon
                      size={42}
                      radius="md"
                      variant="light"
                      color="blue"
                    >
                      <IconUsers size={22} />
                    </ThemeIcon>
                    <Box>
                      <Text size="sm" c="dimmed" fw={500} mb={4}>
                        Total Reps
                      </Text>
                      <Text fz="2xl" fw={700}>
                        {totalReps}
                      </Text>
                    </Box>
                    <Text size="sm" c="dimmed">
                      Active members
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                <Card padding="xl" radius="lg" withBorder h="100%">
                  <Stack gap="lg" justify="space-between" h="100%">
                    <ThemeIcon
                      size={42}
                      radius="md"
                      variant="light"
                      color="teal"
                    >
                      <IconTrendingUp size={22} />
                    </ThemeIcon>
                    <Box>
                      <Text size="sm" c="dimmed" fw={500} mb={4}>
                        Projected Revenue
                      </Text>
                      <Text fz="2xl" fw={700}>
                        {formatCurrency(projectedRevenue)}
                      </Text>
                    </Box>
                    <Text size="sm" c="dimmed">
                      Total across all reps
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>

              {showPayDetails ? (
                <>
                  <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                    <Card padding="xl" radius="lg" withBorder h="100%">
                      <Stack gap="lg" justify="space-between" h="100%">
                        <ThemeIcon
                          size={42}
                          radius="md"
                          variant="light"
                          color="violet"
                        >
                          <IconChartLine size={22} />
                        </ThemeIcon>
                        <Box>
                          <Text size="sm" c="dimmed" fw={500} mb={4}>
                            Avg Projected Margin
                          </Text>
                          <Text fz="2xl" fw={700}>
                            {formatPercentage(adjustedMargin)}
                          </Text>
                        </Box>
                        <Text size="sm" c="dimmed">
                          Across direct reports
                        </Text>
                      </Stack>
                    </Card>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                    <Card padding="xl" radius="lg" withBorder h="100%">
                      <Stack gap="lg" justify="space-between" h="100%">
                        <ThemeIcon
                          size={42}
                          radius="md"
                          variant="light"
                          color="green"
                        >
                          <IconCurrencyDollar size={22} />
                        </ThemeIcon>
                        <Box>
                          <Text size="sm" c="dimmed" fw={500} mb={4}>
                            Projected Earnings
                          </Text>
                          <Text fz="2xl" fw={700}>
                            {formatCurrency(adjustedEarnings)}
                          </Text>
                        </Box>
                        <Text size="sm" c="dimmed">
                          Your estimated take
                        </Text>
                      </Stack>
                    </Card>
                  </Grid.Col>
                </>
              ) : (
                <>
                  <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                    <Card
                      padding="xl"
                      radius="lg"
                      withBorder
                      h="100%"
                      style={{ cursor: "pointer", transition: "all 0.2s" }}
                      onClick={() => handleCardClick("active")}
                    >
                      <Stack gap="lg" justify="space-between" h="100%">
                        <ThemeIcon
                          size={42}
                          radius="md"
                          variant={filterType === "active" ? "filled" : "light"}
                          color="violet"
                        >
                          <IconUserCheck size={22} />
                        </ThemeIcon>
                        <Box>
                          <Text size="sm" c="dimmed" fw={500} mb={4}>
                            Active Reps
                          </Text>
                          <Text fz="2xl" fw={700}>
                            {totalActiveReps}
                          </Text>
                        </Box>
                        <Text size="sm" c="dimmed">
                          Currently active
                        </Text>
                      </Stack>
                    </Card>
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
                    <Card
                      padding="xl"
                      radius="lg"
                      withBorder
                      h="100%"
                      style={{ cursor: "pointer", transition: "all 0.2s" }}
                      onClick={() => handleCardClick("pending")}
                    >
                      <Stack gap="lg" justify="space-between" h="100%">
                        <ThemeIcon
                          size={42}
                          radius="md"
                          variant={
                            filterType === "pending" ? "filled" : "light"
                          }
                          color="orange"
                        >
                          <IconClock size={22} />
                        </ThemeIcon>
                        <Box>
                          <Text size="sm" c="dimmed" fw={500} mb={4}>
                            Pending Reps
                          </Text>
                          <Text fz="2xl" fw={700}>
                            {totalPendingReps}
                          </Text>
                        </Box>
                        <Text size="sm" c="dimmed">
                          Awaiting activation
                        </Text>
                      </Stack>
                    </Card>
                  </Grid.Col>
                </>
              )}
            </Grid>
          )}

          {/* Sales Representatives Section */}
          <Card padding={0} radius="md" withBorder>
            <Stack gap="lg">
              <Group justify="space-between" align="center" p="lg" pb="md">
                <Title order={3}>Sales Representatives</Title>
                <Group gap="sm">
                  <TextInput
                    placeholder="Search reps by name..."
                    leftSection={<IconSearch size={16} />}
                    rightSection={
                      searchQuery && (
                        <ActionIcon
                          size="sm"
                          variant="transparent"
                          onClick={() => setSearchQuery("")}
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      )
                    }
                    value={searchQuery}
                    onChange={event =>
                      setSearchQuery(event.currentTarget.value)
                    }
                    style={{ minWidth: 250 }}
                  />
                  <Button
                    variant={showPayDetails ? "filled" : "outline"}
                    leftSection={
                      showPayDetails ? (
                        <IconEye size={16} />
                      ) : (
                        <IconEyeOff size={16} />
                      )
                    }
                    onClick={handlePayDetailsToggle}
                  >
                    {showPayDetails ? "Hide" : "Show"} Pay Details
                  </Button>

                  {showPayDetails && (
                    <SegmentedControl
                      size="sm"
                      value={payMode}
                      onChange={setPayMode}
                      data={[
                        { label: "Current", value: "current" },
                        { label: "Projected", value: "projected" },
                      ]}
                    />
                  )}

                  {showPayDetails ? (
                    <Menu shadow="md" width={240}>
                      <Menu.Target>
                        <Button
                          variant="outline"
                          rightSection={<IconChevronDown size={16} />}
                          leftSection={<IconSettings size={16} />}
                        >
                          Pay Conditions
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown
                        style={{
                          border:
                            "1px solid var(--mantine-color-default-border)",
                          borderRadius: "var(--mantine-radius-sm)",
                        }}
                      >
                        <Menu.Item
                          leftSection={<IconPercentage size={16} />}
                          onClick={handleOpenPayConditionsModal}
                        >
                          Set Cost Percentages
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Label>Current Settings</Menu.Label>
                        <Menu.Item
                          onClick={handleOpenPayConditionsModal}
                          style={{ cursor: "pointer" }}
                        >
                          Incentive Cost:{" "}
                          {formatPercentage(incentiveCostPercent)}
                        </Menu.Item>
                        <Menu.Item
                          onClick={handleOpenPayConditionsModal}
                          style={{ cursor: "pointer" }}
                        >
                          Buffer Cost: {formatPercentage(bufferCostPercent)}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  ) : (
                    <Menu shadow="md" width={220}>
                      <Menu.Target>
                        <Button
                          variant="outline"
                          rightSection={<IconChevronDown size={16} />}
                        >
                          Templates
                        </Button>
                      </Menu.Target>
                      <Menu.Dropdown
                        style={{
                          border:
                            "1px solid var(--mantine-color-default-border)",
                          borderRadius: "var(--mantine-radius-sm)",
                        }}
                      >
                        <Menu.Item
                          onClick={() => setIsDefaultPayScalesModalOpen(true)}
                        >
                          Default Pay Scales
                        </Menu.Item>
                        <Menu.Item
                          onClick={() => setIsDefaultOverridesModalOpen(true)}
                        >
                          Default Overrides
                        </Menu.Item>
                        <Menu.Item
                          onClick={() => setIsDefaultMDModalOpen(true)}
                        >
                          Default Marketing Deal
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )}

                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <Button
                        variant="light"
                        leftSection={<IconSparkles size={16} />}
                        rightSection={<IconChevronDown size={16} />}
                      >
                        Import
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown
                      style={{
                        border: "1px solid var(--mantine-color-default-border)",
                        borderRadius: "var(--mantine-radius-sm)",
                      }}
                    >
                      <Menu.Item onClick={() => navigate("/reps/import")}>
                        Deals
                      </Menu.Item>
                      <Menu.Item
                        onClick={() => navigate("/reps/import-revenue")}
                      >
                        Revenue
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>

                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => navigate("/reps/add")}
                  >
                    Add Rep
                  </Button>
                </Group>
              </Group>

              {/* Season Filter Section */}
              {seasons.length > 0 && (
                <Box px="lg" pb="md">
                  <Group justify="space-between" align="center" mb="xs">
                    <Text size="sm" fw={600} c="dimmed">
                      Filter by Season
                    </Text>
                    {!seasonsLoading && seasons.length > 1 && (
                      <Group gap="xs">
                        <Button
                          size="xs"
                          variant="outline"
                          color="orange"
                          onClick={handleSelectAllSeasons}
                          styles={{
                            root: {
                              fontWeight: 600,
                            },
                          }}
                        >
                          Select All
                        </Button>
                        <Button
                          size="xs"
                          variant="outline"
                          color="orange"
                          onClick={handleDeselectAllSeasons}
                          styles={{
                            root: {
                              fontWeight: 600,
                            },
                          }}
                        >
                          Deselect All
                        </Button>
                      </Group>
                    )}
                  </Group>
                  {seasonsLoading ? (
                    <Group gap="xs">
                      <Loader size="xs" />
                      <Text size="sm" c="dimmed">
                        Loading seasons...
                      </Text>
                    </Group>
                  ) : (
                    <Group gap="xs">
                      {seasons.map(season => (
                        <Button
                          key={season.id}
                          size="xs"
                          radius="xl"
                          variant={
                            selectedSeasonIds.includes(season.id)
                              ? "filled"
                              : "outline"
                          }
                          color="orange"
                          onClick={() => handleSeasonToggle(season.id)}
                          styles={{
                            root: {
                              transition: "all 0.2s ease",
                              ...(selectedSeasonIds.includes(season.id) && {
                                backgroundColor: "#d0521f",
                                borderColor: "#d0521f",
                                "&:hover": {
                                  backgroundColor: "#b9491c",
                                  borderColor: "#b9491c",
                                },
                              }),
                              ...(!selectedSeasonIds.includes(season.id) && {
                                borderColor: "#d0521f",
                                color: "#d0521f",
                                "&:hover": {
                                  backgroundColor: "rgba(208, 82, 31, 0.05)",
                                  borderColor: "#b9491c",
                                  color: "#b9491c",
                                },
                              }),
                            },
                          }}
                        >
                          {season.name}
                        </Button>
                      ))}
                    </Group>
                  )}
                </Box>
              )}

              {loading && (
                <Group justify="center" py="xl">
                  <Loader size="md" />
                  <Text>Loading reps...</Text>
                </Group>
              )}

              {error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Error"
                  color="red"
                >
                  <Group justify="space-between">
                    <Text>{error}</Text>
                    <Button
                      variant="outline"
                      size="sm"
                      leftSection={<IconRefresh size={16} />}
                      onClick={fetchReps}
                    >
                      Retry
                    </Button>
                  </Group>
                </Alert>
              )}

              {!loading && !error && (
                <Box>
                  {reps.length === 0 ? (
                    <Text ta="center" c="dimmed" py="xl">
                      No reps found. Add your first rep to get started.
                    </Text>
                  ) : filteredReps.length === 0 ? (
                    <Text ta="center" c="dimmed" py="xl">
                      No reps found matching "{searchQuery}". Try a different
                      search term.
                    </Text>
                  ) : (
                    <RepCardList
                      reps={sortedReps}
                      showPayDetails={showPayDetails}
                      payMode={payMode}
                      onResendInvite={handleResendInvite}
                      onViewDetails={(repId, rep) => {
                        setDetailRep(rep ?? null);
                        setDetailRepId(repId);
                      }}
                      onPrefetch={repId =>
                        prefetchRepDetails(repId, getVerifiedJWT)
                      }
                      onPitchReview={handlePitchReview}
                      resendingInvite={resendingInvite}
                      onSetBackendEligible={handleSetBackendEligible}
                    />
                  )}
                </Box>
              )}
            </Stack>
          </Card>
        </Stack>
      )}

      {/* Revenue Tab */}
      {activeTab === "revenue" && <RevenueTab />}

      {activeTab === "budget" && <BudgetTab />}

      {/* Rep Health tab - keep mounted so it doesn't refetch/reload on every tab switch */}
      <div style={{ display: activeTab === "rep-health" ? "block" : "none" }}>
        <RepHealthTab />
      </div>

      {/* Training Stats Tab - always kept mounted so data isn't re-fetched on every tab switch */}
      <div
        style={{ display: activeTab === "training-stats" ? "block" : "none" }}
      >
        <RepTrainingPage />
      </div>

      {/* Program Tracking Tab */}
      {activeTab === "programs" && (
        <Stack gap="lg">
          {/* ── Program Tracking KPI Cards ── */}
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
              <Card padding="xl" radius="lg" withBorder h="100%">
                <Stack gap="lg" justify="space-between" h="100%">
                  <ThemeIcon size={42} radius="md" variant="light" color="teal">
                    <IconTargetArrow size={22} />
                  </ThemeIcon>
                  <Box>
                    <Text size="sm" c="dimmed" fw={500} mb={4}>
                      Program Completion Rate
                    </Text>
                    <Text fz="2xl" fw={700}>
                      {programLoading
                        ? "—"
                        : `${programTrackingMetrics.completionRate}%`}
                    </Text>
                  </Box>
                  <Text size="sm" c="dimmed">
                    Completed reps across all programs
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
              <Card padding="xl" radius="lg" withBorder h="100%">
                <Stack gap="lg" justify="space-between" h="100%">
                  <ThemeIcon size={42} radius="md" variant="light" color="blue">
                    <IconUsers size={22} />
                  </ThemeIcon>
                  <Box>
                    <Text size="sm" c="dimmed" fw={500} mb={4}>
                      Active Learners (7d)
                    </Text>
                    <Text fz="2xl" fw={700}>
                      {programLoading
                        ? "—"
                        : programTrackingMetrics.activeLearners7d}
                    </Text>
                  </Box>
                  <Text size="sm" c="dimmed">
                    Reps with recent program activity
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
              <Card padding="xl" radius="lg" withBorder h="100%">
                <Stack gap="lg" justify="space-between" h="100%">
                  <ThemeIcon
                    size={42}
                    radius="md"
                    variant="light"
                    color="orange"
                  >
                    <IconAlertCircle size={22} />
                  </ThemeIcon>
                  <Box>
                    <Text size="sm" c="dimmed" fw={500} mb={4}>
                      At-Risk Reps
                    </Text>
                    <Text fz="2xl" fw={700}>
                      {programLoading ? "—" : programTrackingMetrics.atRiskReps}
                    </Text>
                  </Box>
                  <Text size="sm" c="dimmed">
                    No activity in 14+ days or not started
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
              <Card padding="xl" radius="lg" withBorder h="100%">
                <Stack gap="lg" justify="space-between" h="100%">
                  <ThemeIcon
                    size={42}
                    radius="md"
                    variant="light"
                    color="grape"
                  >
                    <IconClock size={22} />
                  </ThemeIcon>
                  <Box>
                    <Text size="sm" c="dimmed" fw={500} mb={4}>
                      Avg Time to Complete Program
                    </Text>
                    {programLoading ? (
                      <Text fz="2xl" fw={700}>
                        —
                      </Text>
                    ) : programAvgDaysLoading ? (
                      <Group gap="xs" align="center">
                        <Loader size="xs" color="grape" />
                        <Text fz="sm" c="dimmed" fw={500}>
                          Computing…
                        </Text>
                      </Group>
                    ) : (
                      <Text
                        fz={
                          programTrackingMetrics.avgDaysToCompleteProgram ===
                          null
                            ? "md"
                            : "2xl"
                        }
                        fw={700}
                        c={
                          programTrackingMetrics.avgDaysToCompleteProgram ===
                          null
                            ? "dimmed"
                            : undefined
                        }
                      >
                        {programTrackingMetrics.avgDaysToCompleteProgram ===
                        null
                          ? "None yet"
                          : `${programTrackingMetrics.avgDaysToCompleteProgram}d`}
                      </Text>
                    )}
                  </Box>
                  <Text size="sm" c="dimmed">
                    From first completed task to full completion
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>

          {/* Programs Overview Table */}
          <Paper withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
            <Stack gap="lg">
              <Group justify="space-between" align="center" p="lg" pb="md">
                <Title order={3}>Programs Overview</Title>
              </Group>
              {programLoading ? (
                <Group justify="center" py="xl">
                  <Loader size="md" />
                </Group>
              ) : programError ? (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Error"
                  color="red"
                >
                  {programError}
                </Alert>
              ) : !programData?.programs ||
                programData.programs.length === 0 ? (
                <Paper p="xl" ta="center" bg="gray.0">
                  <Text size="lg" fw={600} mb="xs">
                    No Programs Found
                  </Text>
                  <Text size="sm" c="dimmed">
                    Create programs in the Admin tab to track rep progress
                  </Text>
                </Paper>
              ) : (
                <Box style={{ overflow: "hidden" }}>
                  <Table
                    highlightOnHover
                    verticalSpacing="lg"
                    horizontalSpacing="lg"
                  >
                    <Table.Thead>
                      <Table.Tr>
                        {[
                          "Program Name",
                          "Not Started",
                          "In Progress",
                          "Completed",
                          "Avg Progress",
                          "Completion Rate",
                          "Actions",
                        ].map(h => (
                          <Table.Th
                            key={h}
                            style={{
                              background: "#fafbfc",
                              borderTop: "1px solid #e5e7eb",
                              borderBottom: "1px solid #e5e7eb",
                            }}
                          >
                            {h}
                          </Table.Th>
                        ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {programData.programs.map(program => {
                        const notStartedReps = program.not_started_reps || 0;
                        const inProgressReps = program.in_progress_reps || 0;
                        const completedReps = program.completed_reps || 0;
                        const completionRate =
                          program.enrolled_reps > 0
                            ? Math.round(
                                (completedReps / program.enrolled_reps) * 100
                              )
                            : 0;
                        const isExpanded = expandedProgramId === program.id;
                        const repRows =
                          programData.program_reps?.[program.id] || [];
                        return (
                          <React.Fragment key={program.id}>
                            <Table.Tr
                              style={{
                                cursor: "pointer",
                                backgroundColor: isExpanded
                                  ? "#f8f9fa"
                                  : undefined,
                              }}
                              onClick={() =>
                                setExpandedProgramId(
                                  isExpanded ? null : program.id
                                )
                              }
                            >
                              <Table.Td>
                                <Group gap="sm" align="center" wrap="nowrap">
                                  {program.cover_image ? (
                                    <Box
                                      style={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: 10,
                                        flexShrink: 0,
                                        overflow: "hidden",
                                        border: "1px solid #e9ecef",
                                      }}
                                    >
                                      <img
                                        src={program.cover_image}
                                        alt=""
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <ThemeIcon
                                      size={54}
                                      radius="md"
                                      variant="light"
                                      color="orange"
                                      style={{ flexShrink: 0 }}
                                    >
                                      <IconTargetArrow size={26} />
                                    </ThemeIcon>
                                  )}
                                  <Text size="md" fw={700}>
                                    {program.program_name}
                                  </Text>
                                </Group>
                              </Table.Td>
                              <Table.Td>
                                <Box
                                  style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "50%",
                                    backgroundColor: "#f1f3f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Text
                                    style={{ fontSize: 18, fontWeight: 800 }}
                                    c="dimmed"
                                  >
                                    {notStartedReps}
                                  </Text>
                                </Box>
                              </Table.Td>
                              <Table.Td>
                                <Box
                                  style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "50%",
                                    backgroundColor: "#fff9db",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Text
                                    style={{ fontSize: 18, fontWeight: 800 }}
                                    c="yellow.7"
                                  >
                                    {inProgressReps}
                                  </Text>
                                </Box>
                              </Table.Td>
                              <Table.Td>
                                <Box
                                  style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "50%",
                                    backgroundColor: "#d3f9d8",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Text
                                    style={{ fontSize: 18, fontWeight: 800 }}
                                    c={completedReps > 0 ? "teal.7" : "dimmed"}
                                  >
                                    {completedReps}
                                  </Text>
                                </Box>
                              </Table.Td>
                              <Table.Td>
                                <Text style={{ fontSize: 22 }} fw={700}>
                                  {program.completion_percentage}%
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text
                                  style={{ fontSize: 22 }}
                                  fw={700}
                                  c={completionRate > 0 ? "teal.7" : "dimmed"}
                                >
                                  {completionRate}%
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <ThemeIcon
                                  size={24}
                                  radius="xl"
                                  variant="light"
                                  color="orange"
                                  style={{ cursor: "pointer" }}
                                  onClick={e => {
                                    e.stopPropagation();
                                    setExpandedProgramId(
                                      isExpanded ? null : program.id
                                    );
                                  }}
                                >
                                  {isExpanded ? (
                                    <IconChevronDown size={14} />
                                  ) : (
                                    <IconChevronRight size={14} />
                                  )}
                                </ThemeIcon>
                              </Table.Td>
                            </Table.Tr>
                            {isExpanded && (
                              <Table.Tr>
                                <Table.Td
                                  colSpan={7}
                                  style={{
                                    padding: 0,
                                    backgroundColor: "#f8f9fa",
                                  }}
                                >
                                  <Box p="lg">
                                    <Text fw={600} size="sm" mb="sm" c="dimmed">
                                      Rep Breakdown — {program.program_name}
                                    </Text>
                                    <Table withRowBorders verticalSpacing="sm">
                                      <Table.Thead>
                                        <Table.Tr>
                                          {[
                                            "Rep",
                                            "Status",
                                            "Tasks Completed",
                                            "Progress",
                                            "Last Activity",
                                          ].map(h => (
                                            <Table.Th
                                              key={h}
                                              style={{
                                                background: "#f1f3f5",
                                              }}
                                            >
                                              {h}
                                            </Table.Th>
                                          ))}
                                        </Table.Tr>
                                      </Table.Thead>
                                      <Table.Tbody>
                                        {repRows.length === 0 ? (
                                          <Table.Tr>
                                            <Table.Td colSpan={4}>
                                              <Text
                                                c="dimmed"
                                                size="sm"
                                                ta="center"
                                                py="md"
                                              >
                                                No reps enrolled
                                              </Text>
                                            </Table.Td>
                                          </Table.Tr>
                                        ) : (
                                          repRows.map((rep, i) => (
                                            <Table.Tr
                                              key={i}
                                              style={{
                                                cursor: rep.user_season_id
                                                  ? "pointer"
                                                  : undefined,
                                              }}
                                              onClick={() => {
                                                if (!rep.user_season_id) return;
                                                setRepDetailModal({
                                                  repName: rep.rep_name,
                                                  userSeasonId:
                                                    rep.user_season_id,
                                                  programId: program.id,
                                                  programName:
                                                    program.program_name,
                                                  lastActivity:
                                                    rep.last_activity_at,
                                                });
                                                fetchRepDetail(
                                                  program.id,
                                                  rep.user_season_id
                                                );
                                              }}
                                            >
                                              <Table.Td>
                                                <Text fw={600} size="sm">
                                                  {rep.rep_name}
                                                </Text>
                                              </Table.Td>
                                              <Table.Td>
                                                <Badge
                                                  variant="light"
                                                  color={
                                                    rep.status === "completed"
                                                      ? "teal"
                                                      : rep.status ===
                                                          "in_progress"
                                                        ? "yellow"
                                                        : "gray"
                                                  }
                                                >
                                                  {rep.status === "completed"
                                                    ? "Completed"
                                                    : rep.status ===
                                                        "in_progress"
                                                      ? "In Progress"
                                                      : "Not Started"}
                                                </Badge>
                                              </Table.Td>
                                              <Table.Td>
                                                <Text size="sm">
                                                  {rep.completed_tasks} /{" "}
                                                  {rep.total_tasks}
                                                </Text>
                                              </Table.Td>
                                              <Table.Td>
                                                <Group gap="sm" align="center">
                                                  <Progress
                                                    value={
                                                      rep.total_tasks > 0
                                                        ? Math.round(
                                                            (rep.completed_tasks /
                                                              rep.total_tasks) *
                                                              100
                                                          )
                                                        : 0
                                                    }
                                                    size="sm"
                                                    radius="xl"
                                                    color={
                                                      rep.status === "completed"
                                                        ? "teal"
                                                        : rep.status ===
                                                            "in_progress"
                                                          ? "yellow"
                                                          : "gray"
                                                    }
                                                    style={{
                                                      flex: 1,
                                                      minWidth: 80,
                                                    }}
                                                  />
                                                  <Text
                                                    size="sm"
                                                    fw={600}
                                                    style={{ width: 36 }}
                                                  >
                                                    {rep.total_tasks > 0
                                                      ? Math.round(
                                                          (rep.completed_tasks /
                                                            rep.total_tasks) *
                                                            100
                                                        )
                                                      : 0}
                                                    %
                                                  </Text>
                                                </Group>
                                              </Table.Td>
                                              <Table.Td>
                                                <Text
                                                  size="sm"
                                                  c={
                                                    rep.last_activity_at
                                                      ? undefined
                                                      : "dimmed"
                                                  }
                                                >
                                                  {rep.last_activity_at
                                                    ? (() => {
                                                        const days = Math.floor(
                                                          (Date.now() -
                                                            new Date(
                                                              rep.last_activity_at
                                                            ).getTime()) /
                                                            86400000
                                                        );
                                                        if (days === 0)
                                                          return "Today";
                                                        if (days === 1)
                                                          return "1 day ago";
                                                        return `${days} days ago`;
                                                      })()
                                                    : "—"}
                                                </Text>
                                              </Table.Td>
                                            </Table.Tr>
                                          ))
                                        )}
                                      </Table.Tbody>
                                    </Table>
                                  </Box>
                                </Table.Td>
                              </Table.Tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Box>
              )}
            </Stack>
          </Paper>
        </Stack>
      )}

      {false && false && (
        <Box px="xl" py="lg" style={{ display: "none" }}>
          {/* Header Section */}
          <Group justify="flex-end" align="center" mb="lg">
            <Group gap="xs">
              <ActionIcon
                variant="subtle"
                size="lg"
                color="gray"
                onClick={() => setProgramPage(Math.max(0, programPage - 1))}
                disabled={programPage === 0}
              >
                <IconChevronLeft size={18} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                size="lg"
                color="gray"
                onClick={() => {
                  const totalPages = Math.ceil(
                    (programData?.programs?.length || 0) / PROGRAMS_PER_PAGE
                  );
                  setProgramPage(Math.min(totalPages - 1, programPage + 1));
                }}
                disabled={
                  !programData?.programs ||
                  programPage >=
                    Math.ceil(programData.programs.length / PROGRAMS_PER_PAGE) -
                      1
                }
              >
                <IconChevronRight size={18} />
              </ActionIcon>
              <Button
                variant="subtle"
                leftSection={<IconRefresh size={16} />}
                onClick={fetchProgramData}
                loading={programLoading}
                size="sm"
              >
                Refresh
              </Button>
            </Group>
          </Group>

          <Grid
            gutter="xl"
            align="flex-start"
            style={{ height: "calc(100% - 60px)" }}
          >
            {/* Left Column - Tasks Awaiting Approval */}
            <Grid.Col
              span={{ base: 12, md: 5 }}
              style={{ display: "flex", height: "100%", minHeight: "700px" }}
            >
              <Stack gap="md" style={{ flex: 1 }}>
                {/* Metrics Cards */}
                <Grid gutter="sm">
                  <Grid.Col span={4}>
                    <Paper
                      p="sm"
                      withBorder
                      radius="md"
                      bg="white"
                      style={{ height: "100%" }}
                    >
                      <Stack gap={4}>
                        <Group gap={6}>
                          <IconTargetArrow size={16} color="#228be6" />
                          <Text size="xs" c="dimmed" fw={500}>
                            Active Programs
                          </Text>
                        </Group>
                        <Text size="xl" fw={700} c="dark">
                          {programData?.programs?.length || 0}
                        </Text>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Paper
                      p="sm"
                      withBorder
                      radius="md"
                      bg="white"
                      style={{ height: "100%" }}
                    >
                      <Stack gap={4}>
                        <Group gap={6}>
                          <IconChartLine size={16} color="#228be6" />
                          <Text size="xs" c="dimmed" fw={500}>
                            Overall Completion
                          </Text>
                        </Group>
                        <Text size="xl" fw={700} c="dark">
                          {programData?.programs
                            ? Math.round(
                                programData.programs.reduce(
                                  (sum, p) => sum + p.completion_percentage,
                                  0
                                ) / programData.programs.length
                              )
                            : 0}
                          %
                        </Text>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Paper
                      p="sm"
                      withBorder
                      radius="md"
                      bg="white"
                      style={{ height: "100%" }}
                    >
                      <Stack gap={4}>
                        <Group gap={6}>
                          <IconActivity size={16} color="#228be6" />
                          <Text size="xs" c="dimmed" fw={500}>
                            Awaiting Approval
                          </Text>
                        </Group>
                        <Text size="xl" fw={700} c="dark">
                          {programData?.pending_approvals?.length || 0}
                        </Text>
                      </Stack>
                    </Paper>
                  </Grid.Col>
                </Grid>

                {/* Tasks Awaiting Approval Section */}
                <Paper
                  padding="lg"
                  radius="md"
                  withBorder
                  bg="white"
                  shadow="sm"
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "620px",
                  }}
                >
                  <Stack gap="xs" pt="md" pl="md">
                    <Group gap="xs">
                      <IconClock size={20} style={{ color: "#f59f00" }} />
                      <Text size="md" fw={600}>
                        Tasks Awaiting Approval
                      </Text>
                    </Group>
                    <Text size="sm" c="dimmed">
                      Review and approve submissions
                    </Text>

                    {programLoading ? (
                      <Group justify="center" py="xl">
                        <Loader size="md" />
                        <Text c="dimmed">Loading...</Text>
                      </Group>
                    ) : programError ? (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        title="Error"
                        color="red"
                      >
                        {programError}
                      </Alert>
                    ) : !programData?.pending_approvals ||
                      programData.pending_approvals.length === 0 ? (
                      <Stack align="center" justify="center" py="xl" gap="md">
                        <Box
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            backgroundColor: "#d3f9d8",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <IconCheck size={40} color="#2f9e44" stroke={2.5} />
                        </Box>
                        <Stack gap={4} align="center">
                          <Text size="lg" fw={600}>
                            All caught up!
                          </Text>
                          <Text size="sm" c="dimmed">
                            No tasks waiting for approval
                          </Text>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack
                        gap="sm"
                        style={{
                          maxHeight: "calc(100vh - 300px)",
                          overflowY: "auto",
                        }}
                      >
                        {programData.pending_approvals.map(
                          (approval, index) => (
                            <Paper
                              key={index}
                              p="md"
                              withBorder
                              radius="md"
                              bg="white"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleViewDetails(approval)}
                            >
                              <Stack gap="sm">
                                <Group gap="xs" wrap="nowrap">
                                  <IconUsers
                                    size={16}
                                    style={{ color: "#868e96" }}
                                  />
                                  <Text fw={600} size="sm">
                                    {approval.rep_name}
                                  </Text>
                                  {approval.files &&
                                    approval.files.length > 0 && (
                                      <Badge
                                        size="sm"
                                        variant="light"
                                        color="blue"
                                        leftSection={<IconPhoto size={12} />}
                                      >
                                        {approval.files.length} file
                                        {approval.files.length !== 1 ? "s" : ""}
                                      </Badge>
                                    )}
                                </Group>
                                <Text fw={700} size="md">
                                  {approval.task_name}
                                </Text>
                                {approval.text_response && (
                                  <Text size="sm" c="dimmed" lineClamp={2}>
                                    {approval.text_response}
                                  </Text>
                                )}
                                <Group justify="space-between" align="center">
                                  <Text size="xs" c="dimmed">
                                    {approval.program_name} •{" "}
                                    {new Date(
                                      approval.submitted_at
                                    ).toLocaleDateString()}
                                  </Text>
                                </Group>
                                <Button
                                  size="sm"
                                  variant="light"
                                  fullWidth
                                  radius="md"
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleViewDetails(approval);
                                  }}
                                >
                                  View Details & Approve/Reject
                                </Button>
                              </Stack>
                            </Paper>
                          )
                        )}
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Grid.Col>

            {/* Right Column - Program Cards */}
            <Grid.Col
              span={{ base: 12, md: 7 }}
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Box style={{ height: "100%" }}>
                {programLoading ? (
                  <Group justify="center" py="xl">
                    <Loader size="md" />
                  </Group>
                ) : programError ? (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Error"
                    color="red"
                  >
                    {programError}
                  </Alert>
                ) : !programData?.programs ||
                  programData.programs.length === 0 ? (
                  <Paper p="xl" ta="center" withBorder bg="gray.0" shadow="sm">
                    <Text size="lg" fw={600} mb="xs">
                      No Programs Found
                    </Text>
                    <Text size="sm" c="dimmed">
                      Create programs in the Admin tab to track rep progress
                    </Text>
                  </Paper>
                ) : (
                  <Grid style={{ flex: 1, marginTop: 0 }}>
                    {programData.programs
                      .slice(
                        programPage * PROGRAMS_PER_PAGE,
                        (programPage + 1) * PROGRAMS_PER_PAGE
                      )
                      .map(program => {
                        const completedReps = Math.round(
                          (program.completion_percentage / 100) *
                            program.enrolled_reps
                        );
                        const completionRate =
                          program.enrolled_reps > 0
                            ? Math.round(
                                (completedReps / program.enrolled_reps) * 100
                              )
                            : 0;

                        return (
                          <Grid.Col
                            key={program.id}
                            span={{ base: 12, lg: 6 }}
                            style={{ display: "flex", minHeight: "350px" }}
                          >
                            <Paper
                              p="lg"
                              withBorder
                              radius="md"
                              bg="white"
                              shadow="sm"
                              style={{
                                cursor: "pointer",
                                transition: "all 0.2s",
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: "350px",
                              }}
                              onClick={() => {
                                navigate(`/downline/programs/${program.id}`);
                              }}
                            >
                              <Stack
                                gap="md"
                                style={{
                                  flex: 1,
                                  justifyContent: "space-between",
                                }}
                              >
                                {/* Program Name and Description */}
                                <div>
                                  <Text fw={700} size="lg" mb={4}>
                                    {program.program_name}
                                  </Text>
                                  <Text size="sm" c="dimmed">
                                    {program.description ||
                                      "Track completion and engagement"}
                                  </Text>
                                </div>

                                {/* Average Progress */}
                                <div>
                                  <Group justify="space-between" mb={8}>
                                    <Text size="sm" fw={500} c="dimmed">
                                      Average Progress
                                    </Text>
                                    <Text size="sm" fw={700}>
                                      {program.completion_percentage}%
                                    </Text>
                                  </Group>
                                  <Progress
                                    value={program.completion_percentage}
                                    size="md"
                                    radius="xl"
                                    color="dark"
                                  />
                                </div>

                                {/* Enrolled and Completed Stats */}
                                <Grid gutter="md">
                                  <Grid.Col span={6}>
                                    <Stack gap={4}>
                                      <Group gap={6}>
                                        <IconUsers size={14} color="#868e96" />
                                        <Text size="xs" c="dimmed">
                                          Enrolled
                                        </Text>
                                      </Group>
                                      <Text size="xl" fw={700} lh={1}>
                                        {program.enrolled_reps}
                                      </Text>
                                      <Text size="xs" c="dimmed">
                                        reps enrolled
                                      </Text>
                                    </Stack>
                                  </Grid.Col>
                                  <Grid.Col span={6}>
                                    <Stack gap={4}>
                                      <Group gap={6}>
                                        <IconCheck size={14} color="#868e96" />
                                        <Text size="xs" c="dimmed">
                                          Completed
                                        </Text>
                                      </Group>
                                      <Text size="xl" fw={700} lh={1}>
                                        {completedReps}
                                      </Text>
                                      <Text size="xs" c="dimmed">
                                        {completionRate}% completion rate
                                      </Text>
                                    </Stack>
                                  </Grid.Col>
                                </Grid>

                                {/* View Details Button */}
                                <Button
                                  variant="subtle"
                                  fullWidth
                                  rightSection={<IconChevronDown size={16} />}
                                  size="sm"
                                  styles={{
                                    root: {
                                      border: "1px solid #e9ecef",
                                      "&:hover": {
                                        backgroundColor: "#f8f9fa",
                                      },
                                    },
                                  }}
                                >
                                  View Details
                                </Button>
                              </Stack>
                            </Paper>
                          </Grid.Col>
                        );
                      })}
                  </Grid>
                )}
              </Box>
            </Grid.Col>
          </Grid>
        </Box>
      )}

      {/* Pitch Reviews Tab */}
      {activeTab === "pitch-reviews" && <PitchReviewPanel />}

      {/* Events Tab */}
      {activeTab === "events" && (
        <Stack gap="lg">
          <Grid gutter="xs">
            {[
              {
                label: "Total Events",
                value: eventsOverviewMetrics.totalEvents,
                icon: IconCalendarEvent,
                iconColor: "#228be6",
                iconBg: "#e7f5ff",
              },
              {
                label: "Upcoming Events",
                value: eventsOverviewMetrics.upcomingEvents,
                icon: IconClock,
                iconColor: "#0ca678",
                iconBg: "#e6fcf5",
              },
              {
                label: "Past Events",
                value: eventsOverviewMetrics.pastEvents,
                icon: IconClipboardList,
                iconColor: "#f59f00",
                iconBg: "#fff9db",
              },
              {
                label: "Attendance Check-ins",
                value: eventsOverviewMetrics.attendanceCheckIns,
                icon: IconUserCheck,
                iconColor: "#7048e8",
                iconBg: "#f3f0ff",
              },
            ].map(({ label, value, icon: Icon, iconColor, iconBg }) => (
              <Grid.Col key={label} span={{ base: 12, sm: 6, lg: 3 }}>
                <Card
                  padding="xl"
                  radius="lg"
                  withBorder
                  h="100%"
                  style={{ border: "1px solid #e9ecef" }}
                >
                  <Stack
                    gap="lg"
                    align="center"
                    justify="space-between"
                    h="100%"
                    style={{ textAlign: "center" }}
                  >
                    <Box
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        background: iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto",
                      }}
                    >
                      <Icon size={18} color={iconColor} />
                    </Box>
                    <Text
                      size="md"
                      c="dimmed"
                      fw={600}
                      ta="center"
                      lineClamp={1}
                    >
                      {label}
                    </Text>
                    <Text
                      fw={800}
                      ta="center"
                      style={{ fontSize: 28, lineHeight: 1.1, marginTop: 6 }}
                    >
                      {value}
                    </Text>
                  </Stack>
                </Card>
              </Grid.Col>
            ))}
          </Grid>

          <Grid gutter="lg" align="stretch">
            <Grid.Col span={{ base: 12, md: 9 }}>
              <div
                ref={calendarWrapperRef}
                className="downline-events-calendar"
                style={{ height: "calc(100vh - 120px)" }}
              >
                <DnDCalendar
                  ref={calendarRef}
                  localizer={localizer}
                  events={
                    draftCalendarEvent
                      ? [...calendarEvents, draftCalendarEvent]
                      : calendarEvents
                  }
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: "100%" }}
                  draggableAccessor={event => !event.isDraft}
                  onEventDrop={handleEventDrop}
                  date={currentCalendarDate}
                  view={currentCalendarView}
                  onView={view => setCurrentCalendarView(view)}
                  onNavigate={date => setCurrentCalendarDate(date)}
                  scrollToTime={new Date()}
                  onSelectSlot={slotInfo => {
                    const s = getEventPrefillDateTime(
                      slotInfo?.start || new Date()
                    );
                    const e = slotInfo?.end
                      ? new Date(slotInfo.end)
                      : new Date(s.getTime() + 60 * 60_000);
                    setDraftCalendarEvent({
                      id: "__draft__",
                      title: "(New Event)",
                      start: s,
                      end: e,
                      isDraft: true,
                    });
                    setQuickCreateSlot({ start: s, end: e });
                    setQuickCreateOpen(true);
                  }}
                  onDrillDown={date => {
                    const s = getEventPrefillDateTime(date);
                    const e = new Date(s.getTime() + 60 * 60_000);
                    setDraftCalendarEvent({
                      id: "__draft__",
                      title: "(New Event)",
                      start: s,
                      end: e,
                      isDraft: true,
                    });
                    setQuickCreateSlot({ start: s, end: e });
                    setQuickCreateOpen(true);
                  }}
                  onSelectEvent={event => {
                    setSelectedEvent(event);
                    setQuickCreateSlot({ start: event.start, end: event.end });
                    setQuickCreateOpen(true);
                  }}
                  selectable
                  views={["day", "week", "month"]}
                  defaultView="day"
                  components={{
                    toolbar: function CustomToolbar(toolbar) {
                      const label = () => {
                        const date = moment(toolbar.date);
                        if (toolbar.view === "month") {
                          return date.format("MMMM YYYY");
                        }
                        if (toolbar.view === "week") {
                          const start = date.clone().startOf("week");
                          const end = date.clone().endOf("week");
                          return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
                        }
                        return date.format("dddd, MMMM D, YYYY");
                      };

                      return (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "0.75rem",
                            marginBottom: "1rem",
                            padding: "0.75rem 0 0.5rem",
                            paddingLeft: "0.75rem",
                            paddingRight: "0.75rem",
                          }}
                        >
                          <Group gap="xs">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => toolbar.onNavigate("TODAY")}
                            >
                              Today
                            </Button>
                            <ActionIcon
                              size="lg"
                              variant="subtle"
                              color="gray"
                              onClick={() => toolbar.onNavigate("PREV")}
                            >
                              <IconChevronLeft size={20} />
                            </ActionIcon>
                            <ActionIcon
                              size="lg"
                              variant="subtle"
                              color="gray"
                              onClick={() => toolbar.onNavigate("NEXT")}
                            >
                              <IconChevronRight size={20} />
                            </ActionIcon>
                          </Group>

                          <Box
                            style={{
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #e5e7eb",
                              borderRadius: "999px",
                              padding: "0.5rem 1rem",
                              minWidth: "220px",
                              textAlign: "center",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                            }}
                          >
                            <Text fw={600} size="sm">
                              {label()}
                            </Text>
                          </Box>

                          <Group gap="xs">
                            <Button
                              size="sm"
                              variant={
                                toolbar.view === "day" ? "filled" : "default"
                              }
                              color="orange"
                              onClick={() => toolbar.onView("day")}
                            >
                              Day
                            </Button>
                            <Button
                              size="sm"
                              variant={
                                toolbar.view === "week" ? "filled" : "default"
                              }
                              color="orange"
                              onClick={() => toolbar.onView("week")}
                            >
                              Week
                            </Button>
                            <Button
                              size="sm"
                              variant={
                                toolbar.view === "month" ? "filled" : "default"
                              }
                              color="orange"
                              onClick={() => toolbar.onView("month")}
                            >
                              Month
                            </Button>
                          </Group>
                        </div>
                      );
                    },
                  }}
                  popup
                  eventPropGetter={event => ({
                    style: event.isDraft
                      ? {
                          backgroundColor: "rgba(100, 116, 139, 0.18)",
                          backgroundImage: "none",
                          borderRadius: "4px",
                          border: "2px dashed #94a3b8",
                          display: "block",
                          fontSize: "0.82rem",
                          color: "#475569",
                          cursor: "default",
                        }
                      : {
                          backgroundColor: "var(--mantine-color-orange-6)",
                          backgroundImage: "none",
                          borderRadius: "4px",
                          border: "none",
                          display: "block",
                          fontSize: "0.85rem",
                          color: "white",
                        },
                  })}
                />
              </div>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 3 }}>
              <Card
                withBorder
                radius="xl"
                p="lg"
                h="calc(100vh - 120px)"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  background: "#ffffff",
                  borderColor: "#e2e8f0",
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
                }}
              >
                {/* ── Attendance header ── */}
                <Group
                  justify="space-between"
                  mb="md"
                  wrap="nowrap"
                  align="flex-start"
                >
                  <Box>
                    <Text fw={700} size="lg" c="#0f172a">
                      {attendanceView === "upcoming"
                        ? "Upcoming Events"
                        : "Past Events"}
                    </Text>
                    <Text size="xs" c="#64748b" mt={2}>
                      {attendanceView === "upcoming"
                        ? "Events that still need attendance tracked"
                        : "Previously held attendance events"}
                    </Text>
                  </Box>
                  <Group gap="xs" wrap="nowrap" align="center">
                    <Button
                      size="xs"
                      radius="xl"
                      variant={
                        attendanceView === "upcoming" ? "filled" : "light"
                      }
                      color="orange"
                      onClick={() => setAttendanceView("upcoming")}
                    >
                      Upcoming
                    </Button>
                    <Button
                      size="xs"
                      radius="xl"
                      variant={attendanceView === "past" ? "filled" : "light"}
                      color="orange"
                      onClick={() => setAttendanceView("past")}
                    >
                      Past
                    </Button>
                  </Group>
                </Group>

                {/* ── Attendance list ── */}
                <Stack
                  gap="sm"
                  style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}
                >
                  {filteredAttendanceList.map(evt => {
                    const statusColor =
                      evt.status === "active"
                        ? "teal"
                        : evt.status === "completed"
                          ? "blue"
                          : "gray";
                    const isPastEvent =
                      evt.scheduled_time &&
                      new Date(evt.end_time).getTime() < Date.now();
                    const eventDate = evt.scheduled_time
                      ? moment(evt.scheduled_time)
                      : null;
                    const participantSummary =
                      evt.source === "attendance"
                        ? isPastEvent
                          ? `${attendanceCounts[evt.rawEvent?.id] ?? 0} attended`
                          : `${totalInviteableParticipants} invited`
                        : `${evt.attendees?.length || 1} invited`;
                    const showStatusBadge =
                      evt.status && evt.status !== "active";
                    return (
                      <Paper
                        key={evt.id}
                        p="md"
                        radius="xl"
                        withBorder
                        style={{
                          cursor: "pointer",
                          borderColor: "#cbd5e1",
                          background: "#ffffff",
                          boxShadow: "0 6px 18px rgba(15, 23, 42, 0.06)",
                          transition:
                            "transform 0.15s ease, box-shadow 0.15s ease",
                        }}
                        onClick={() => {
                          if (evt.source === "attendance") {
                            setSelectedAttendanceEvent(evt.rawEvent);
                            return;
                          }

                          setSelectedEvent(evt.rawEvent);
                          setQuickCreateSlot({
                            start: new Date(evt.scheduled_time),
                            end: new Date(evt.end_time),
                          });
                          setQuickCreateOpen(true);
                        }}
                      >
                        <Group align="center" gap="sm" wrap="nowrap">
                          <Box
                            style={{
                              minWidth: 88,
                              height: 114,
                              borderRadius: 16,
                              background: isPastEvent
                                ? "linear-gradient(180deg, #e2e8f0 0%, #cbd5e1 100%)"
                                : "linear-gradient(180deg, #f0c6b5 0%, #e7a58b 100%)",
                              color: "#0f172a",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
                              textAlign: "center",
                              padding: "4px 6px",
                            }}
                          >
                            <Text size="13px" fw={600} tt="uppercase" lh={1.15}>
                              {eventDate ? eventDate.format("MMM") : "—"}
                            </Text>
                            <Text size="28px" fw={700} lh={1}>
                              {eventDate ? eventDate.format("D") : "—"}
                            </Text>
                            <Text size="12px" fw={500} lh={1.15} mt={3}>
                              {eventDate ? eventDate.format("dddd") : "—"}
                            </Text>
                          </Box>

                          <Box
                            style={{
                              flex: 1,
                              minWidth: 0,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              minHeight: 114,
                            }}
                          >
                            <Group
                              justify="space-between"
                              align="center"
                              mb={4}
                            >
                              <Text
                                fw={700}
                                size="md"
                                lineClamp={2}
                                c="#0f172a"
                                style={{ flex: 1 }}
                              >
                                {evt.title}
                              </Text>
                              {showStatusBadge && (
                                <Badge
                                  size="sm"
                                  radius="xl"
                                  variant="light"
                                  color={statusColor}
                                  styles={{
                                    root: { textTransform: "capitalize" },
                                  }}
                                >
                                  {evt.status}
                                </Badge>
                              )}
                            </Group>

                            <Text
                              size="md"
                              c="#64748b"
                              fw={600}
                              mt={6}
                              mb={4}
                              lineClamp={1}
                            >
                              {participantSummary}
                            </Text>

                            <Text size="md" c="#64748b" fw={500}>
                              {eventDate ? eventDate.format("h:mm A") : "—"}
                            </Text>
                          </Box>

                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            radius="xl"
                            mt={2}
                          >
                            <IconArrowRight size={16} />
                          </ActionIcon>
                        </Group>
                      </Paper>
                    );
                  })}

                  {filteredAttendanceList.length === 0 && (
                    <Stack gap="sm" py="xl" align="center">
                      <Text size="sm" c="dimmed" ta="center">
                        {attendanceView === "upcoming"
                          ? "No upcoming attendance events yet."
                          : "No past attendance events yet."}
                      </Text>
                      {attendanceView === "upcoming" && (
                        <Button
                          size="xs"
                          variant="light"
                          color="orange"
                          leftSection={<IconPlus size={12} />}
                          onClick={() => setShowCreateAttendance(true)}
                        >
                          Create First Event
                        </Button>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        </Stack>
      )}

      {/* ── Quick-create popup (calendar drag) ── */}
      <QuickCreateEventPopup
        isOpen={quickCreateOpen}
        slot={quickCreateSlot}
        event={selectedEvent}
        onClose={() => {
          setQuickCreateOpen(false);
          setDraftCalendarEvent(null); // remove ghost on cancel
          setSelectedEvent(null);
        }}
        onEventCreated={async payload => {
          setQuickCreateOpen(false);

          if (payload?.optimisticEvent) {
            setCalendarEvents(prev => [...prev, payload.optimisticEvent]);
          }

          setDraftCalendarEvent(null);
          setSelectedEvent(null);

          const loaded = await fetchTeamEvents();
          if (loaded) {
            setCalendarEvents(prev =>
              prev.filter(event => !event.isPendingCreate)
            );
          }

          fetchAttendanceEvents(); // refresh QR attendance list in case "Create QR code" was checked
        }}
        onMoreOptions={prefill => {
          setDraftCalendarEvent(null); // remove ghost when opening full modal
          setEventPrefillDateTime(prefill?.start || new Date());
          setEventPrefillMeetingType(prefill?.meetingType || "team_meeting");
          setQuickCreateOpen(false);
          setSelectedEvent(prefill?.event || selectedEvent);
          setCreateTeamEventModalOpen(true);
        }}
      />

      <CreateTeamEventModal
        isOpen={createTeamEventModalOpen}
        initialDateTime={eventPrefillDateTime}
        initialMeetingType={eventPrefillMeetingType}
        event={selectedEvent}
        onClose={() => {
          setCreateTeamEventModalOpen(false);
          setEventPrefillDateTime(null);
          setEventPrefillMeetingType("team_meeting");
          setSelectedEvent(null);
        }}
        onEventCreated={() => {
          setCreateTeamEventModalOpen(false);
          setEventPrefillDateTime(null);
          setEventPrefillMeetingType("team_meeting");
          setSelectedEvent(null);
          fetchTeamEvents();
        }}
      />

      <CalendarFilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        currentDate={currentCalendarDate}
        onDateSelect={(startDate, endDate) => {
          setCurrentCalendarDate(startDate);
          if (endDate && startDate.getTime() !== endDate.getTime()) {
            setCurrentCalendarView("week");
          } else {
            setCurrentCalendarView("day");
          }
          setFilterModalOpen(false);
        }}
      />

      {/* Task Approval Detail Modal */}
      <TaskApprovalDetailModal
        approval={selectedApproval}
        isOpen={showApprovalDetailModal}
        onClose={() => {
          setShowApprovalDetailModal(false);
          setSelectedApproval(null);
        }}
        onApprove={handleApproveTask}
        onReject={handleRejectTask}
      />

      {/* Rep Progress Detail Modal */}
      <Modal
        opened={!!repDetailModal}
        onClose={() => {
          setRepDetailModal(null);
          setRepDetailData(null);
        }}
        title={
          repDetailModal && (
            <Group gap="xs">
              <IconUsers size={20} />
              <Title order={3} fw={600}>
                {repDetailModal.repName}
              </Title>
              <Text size="sm" c="dimmed" fw={400}>
                — {repDetailModal.programName}
              </Text>
            </Group>
          )
        }
        size="lg"
        padding="xl"
        scrollAreaComponent={undefined}
      >
        {repDetailLoading ? (
          <Group justify="center" py="xl">
            <Loader size="md" />
            <Text c="dimmed">Loading progress...</Text>
          </Group>
        ) : !repDetailData ? (
          <Text ta="center" c="dimmed" py="xl">
            No progress data found for this rep.
          </Text>
        ) : (
          <Stack gap="lg">
            {/* Progress Overview */}
            <Paper withBorder radius="md" p="lg">
              <Text fw={600} size="md" mb="md">
                Progress Overview
              </Text>
              <Grid gutter="md" mb="md">
                <Grid.Col span={4}>
                  <Stack gap={4}>
                    <Group gap={6}>
                      <IconCheck size={16} color="#2f9e44" />
                      <Text size="xs" c="dimmed" fw={500}>
                        Completed
                      </Text>
                    </Group>
                    <Text size="xl" fw={700}>
                      {repDetailData.completed_tasks_count || 0}
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Stack gap={4}>
                    <Group gap={6}>
                      <IconClipboardList size={16} color="#228be6" />
                      <Text size="xs" c="dimmed" fw={500}>
                        Total Tasks
                      </Text>
                    </Group>
                    <Text size="xl" fw={700}>
                      {repDetailData.total_tasks_count || 0}
                    </Text>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Stack gap={4}>
                    <Group gap={6}>
                      <IconClock size={16} color="#ae3ec9" />
                      <Text size="xs" c="dimmed" fw={500}>
                        Last Activity
                      </Text>
                    </Group>
                    <Text size="sm" fw={600}>
                      {repDetailModal?.lastActivity
                        ? (() => {
                            const days = Math.floor(
                              (Date.now() -
                                new Date(
                                  repDetailModal.lastActivity
                                ).getTime()) /
                                86400000
                            );
                            if (days === 0) return "Today";
                            if (days === 1) return "1 day ago";
                            return `${days} days ago`;
                          })()
                        : "—"}
                    </Text>
                  </Stack>
                </Grid.Col>
              </Grid>
              <Box>
                <Group justify="space-between" mb={6}>
                  <Text size="sm" c="dimmed">
                    Overall Progress
                  </Text>
                  <Text size="sm" fw={700}>
                    {repDetailData.total_tasks_count > 0
                      ? Math.round(
                          (repDetailData.completed_tasks_count /
                            repDetailData.total_tasks_count) *
                            100
                        )
                      : 0}
                    %
                  </Text>
                </Group>
                <Progress
                  value={
                    repDetailData.total_tasks_count > 0
                      ? Math.round(
                          (repDetailData.completed_tasks_count /
                            repDetailData.total_tasks_count) *
                            100
                        )
                      : 0
                  }
                  size="md"
                  radius="xl"
                  color="orange"
                />
              </Box>
            </Paper>

            {/* Task Details */}
            <Box>
              <Text fw={600} size="md" mb="sm">
                Task Details
              </Text>
              <Stack gap="xs">
                {(repDetailData.tasks || []).map((task, idx) => {
                  let TaskIcon = IconClipboardList;
                  if (task.task_type === "VIDEO") TaskIcon = IconPlayerPlay;
                  else if (task.task_type === "DOCUMENT")
                    TaskIcon = IconFileText;
                  return (
                    <Paper
                      key={idx}
                      withBorder
                      radius="md"
                      p="md"
                      style={{
                        borderColor: task.is_completed ? "#d3f9d8" : undefined,
                        backgroundColor: task.is_completed
                          ? "#f4fdf5"
                          : undefined,
                      }}
                    >
                      <Group gap="sm" align="flex-start">
                        <ThemeIcon
                          size={32}
                          radius="md"
                          variant="light"
                          color={task.is_completed ? "teal" : "gray"}
                        >
                          {task.is_completed ? (
                            <IconCheck size={16} />
                          ) : (
                            <TaskIcon size={16} />
                          )}
                        </ThemeIcon>
                        <Box style={{ flex: 1 }}>
                          <Text fw={600} size="sm">
                            {task.task_name}
                          </Text>
                          {task.task_desc && (
                            <Text size="xs" c="dimmed" lineClamp={2} mt={2}>
                              {task.task_desc}
                            </Text>
                          )}
                        </Box>
                        <Badge
                          variant="light"
                          color={task.is_completed ? "teal" : "gray"}
                          size="sm"
                        >
                          {task.is_completed ? "Done" : "Not Done"}
                        </Badge>
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}
      </Modal>

      {/* ── Attendance: Create Event Modal ── */}
      {showCreateAttendance && (
        <CreateEventModal
          event={null}
          onClose={() => setShowCreateAttendance(false)}
          onSaved={newEvent => {
            setAttendanceEvents(prev =>
              [newEvent, ...prev].sort(
                (a, b) =>
                  new Date(b.scheduled_time) - new Date(a.scheduled_time)
              )
            );
            setShowCreateAttendance(false);
          }}
        />
      )}

      {/* ── Attendance: Event Detail Modal (QR + attendees + manual mark) ── */}
      {selectedAttendanceEvent && (
        <AttendanceEventDetailModal
          event={selectedAttendanceEvent}
          onClose={() => setSelectedAttendanceEvent(null)}
          onUpdated={updatedEvent => {
            setAttendanceEvents(prev =>
              prev.map(e => (e.id === updatedEvent.id ? updatedEvent : e))
            );
            setSelectedAttendanceEvent(updatedEvent);
          }}
          onDeleted={deletedId => {
            setAttendanceEvents(prev => prev.filter(e => e.id !== deletedId));
            setSelectedAttendanceEvent(null);
          }}
        />
      )}

      <DefaultScaleModal
        type="COMMISSION"
        isOpen={isDefaultPayScalesModalOpen}
        onClose={() => setIsDefaultPayScalesModalOpen(false)}
      />

      <DefaultScaleModal
        type="OVERRIDE"
        isOpen={isDefaultOverridesModalOpen}
        onClose={() => setIsDefaultOverridesModalOpen(false)}
      />

      <DefaultScaleModal
        type="MD"
        isOpen={isDefaultMDModalOpen}
        onClose={() => setIsDefaultMDModalOpen(false)}
      />

      {/* Pay Conditions Modal */}
      <Modal
        opened={isPayConditionsModalOpen}
        onClose={() => setIsPayConditionsModalOpen(false)}
        title={
          <Group gap="xs">
            <IconSettings size={20} />
            <Title order={3} fw={600}>
              Pay Conditions
            </Title>
          </Group>
        }
        size="md"
        padding="xl"
      >
        <Stack gap="md">
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="Cost Adjustments"
            color="blue"
            variant="light"
          >
            These percentages will be subtracted from the projected margin and
            earnings displayed in the dashboard.
          </Alert>

          <Divider />

          <Stack gap="sm">
            <Text fw={600} size="sm">
              Incentive Cost Percentage
            </Text>
            <Group gap="sm" align="flex-end">
              <TextInput
                type="number"
                placeholder="0"
                value={tempIncentiveCost === 0 ? "" : tempIncentiveCost}
                onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  setTempIncentiveCost(Math.max(0, Math.min(100, val)));
                }}
                min={0}
                max={100}
                step={0.25}
                decimalScale={4}
                leftSection={<IconPercentage size={16} />}
                style={{ flex: 1 }}
              />
              <Text size="sm" c="dimmed">
                %
              </Text>
            </Group>
          </Stack>

          <Stack gap="sm">
            <Text fw={600} size="sm">
              Buffer Cost Percentage
            </Text>
            <Group gap="sm" align="flex-end">
              <TextInput
                type="number"
                placeholder="0"
                value={tempBufferCost === 0 ? "" : tempBufferCost}
                onChange={e => {
                  const val = parseFloat(e.target.value) || 0;
                  setTempBufferCost(Math.max(0, Math.min(100, val)));
                }}
                min={0}
                max={100}
                step={0.25}
                decimalScale={4}
                leftSection={<IconPercentage size={16} />}
                style={{ flex: 1 }}
              />
              <Text size="sm" c="dimmed">
                %
              </Text>
            </Group>
          </Stack>

          <Divider />

          <Alert
            icon={<IconInfoCircle size={16} />}
            color="gray"
            variant="light"
          >
            <Text size="sm" fw={600}>
              Total Cost Adjustment:{" "}
              {formatPercentage(tempIncentiveCost + tempBufferCost)}
            </Text>
          </Alert>

          <Group justify="space-between" gap="sm" pt="md">
            <Button
              variant="outline"
              onClick={() => setIsPayConditionsModalOpen(false)}
              disabled={isSavingPayConditions}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePayConditions}
              leftSection={<IconSettings size={16} />}
              loading={isSavingPayConditions}
              disabled={isSavingPayConditions}
            >
              Save Settings
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* PIN Verification Modal */}
      <Modal
        opened={isPinModalOpen}
        onClose={handleClosePinModal}
        title={
          <Group gap="xs">
            <IconLock size={20} />
            <Title order={3} fw={600}>
              Verify Security PIN
            </Title>
          </Group>
        }
        size="md"
        padding="xl"
        closeOnClickOutside={true}
        closeOnEscape={true}
      >
        <Stack gap="md">
          {pinError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="red"
              variant="light"
            >
              {pinError}
            </Alert>
          )}

          <Alert
            icon={<IconInfoCircle size={16} />}
            title="Secure Information"
            color="blue"
            variant="light"
          >
            Your pay details are protected. Please enter your 4-digit security
            PIN to view projected margin and earnings information.
          </Alert>

          <Divider />

          {/* PIN Input Section */}
          <Stack gap="md" align="center">
            <Group gap="xs">
              <IconShieldCheck size={20} />
              <Text fw={600} size="sm">
                Enter Your PIN
              </Text>
            </Group>
            <PinInput
              length={4}
              value={pin}
              onChange={setPin}
              onComplete={handlePinComplete}
              placeholder="○"
              type="number"
              size="xl"
              disabled={pinVerifying}
              mask
              styles={{
                input: {
                  borderColor: pinError ? "#fa5252" : undefined,
                },
              }}
            />
            {pinVerifying && (
              <Group gap="xs">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                  Verifying PIN...
                </Text>
              </Group>
            )}
          </Stack>

          <Select
            label="Keep unlocked for"
            value={unlockDuration}
            onChange={value =>
              setUnlockDuration(value || String(DEFAULT_PIN_UNLOCK_DURATION))
            }
            data={PIN_UNLOCK_DURATIONS.map(option => ({
              value: String(option.value),
              label: option.label,
            }))}
            allowDeselect={false}
          />

          {/* Action Buttons */}
          <Group justify="space-between" gap="sm" pt="md">
            <Button
              variant="outline"
              onClick={handleClosePinModal}
              disabled={pinVerifying}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleVerifyPin()}
              loading={pinVerifying}
              leftSection={
                pinVerifying ? undefined : <IconShieldCheck size={16} />
              }
              disabled={pin.length !== 4}
              size="sm"
            >
              Verify PIN
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Rep details open in a right-side drawer over the list. */}
      <Drawer
        opened={detailRepId != null}
        onClose={() => {
          setDetailRepId(null);
          // Reflect any edits/deletes made in the panel.
          fetchReps();
        }}
        position="right"
        size={560}
        withCloseButton={false}
        padding={0}
        styles={{ body: { padding: 0, height: "100%" } }}
      >
        {detailRepId != null && (
          <RepDetailsPage
            repId={detailRepId}
            initialRep={detailRep}
            asPanel
            onClose={() => {
              setDetailRepId(null);
              fetchReps();
            }}
          />
        )}
      </Drawer>
    </PageContent>
  );
}

export default DownlineManagementPage;
